import {
	BUILT_IN_BASE_ENTITY_IDS,
	CALLABLE_TYPES,
	CALLER_TYPES,
	CallableEntityState,
	CanvasEntityState,
	ConditionState,
	DefinitionEntityState,
	DataTypeState,
	DataTypeStateEntityOption,
	ENTRY_POINT_TYPES,
	EntityType,
	ILiteralValue,
	InternalCallState,
	OperationState,
	PASS_THROUGH_CALLABLE_TYPES,
	PrimitiveTypes,
	ProjectState,
	TERMINATION_TYPES,
	TerminationEntityState,
	VALUE_READING_TYPES,
	VALUE_WRITING_TYPES,
	VARIABLE_TYPES,
	ValueDescriptorParentChildRelation,
	ValueDescriptorState,
	ValueReadingEntityState,
	ValueWritingEntityState,
	checkAreDataTypesCompatible,
	filterOutDuplicateEntities,
	getParentCanvasEntities,
	ChangeSet,
	getScopeOwner,
	checkScopeCompatibility,
	ScopeCompatibilityType,
	flattenElementCalls,
	checkIsBranchDependentButNotDirectlyOnBranch,
	resolvePersistedDefinitionEntityDatabaseEntity,
	SearchState,
	ActionInputMapState,
	createStateFromTypeAndSubscribe,
	ChangeSetEntityChangeType,
	PrimitiveEntityState,
	ArgumentDeclarationState,
	CallerEntityState,
	EntityState,
	EntryPointEntityState,
	ExecutableEntityState,
	InputMapState,
	LoopState,
	OutputMapState,
	PassThroughCallableEntityState,
	VariableState,
	CanvasEntityConnectionDisabledReason,
	mergeArraysOfStringsWithoutDuplicates,
	getParentCanvasEntity,
	OutputMapParentChildRelation,
	flattenCalls,
	DraggableCallableEntityState,
	ActionDescriptorState,
	ExternalIntegrationOperations,
	BaseEntityNames,
	LiteralValueType,
	UserManagedEntityState,
	EntityId,
	getReferencedIds,
	UserManagedDataTypeParentEntityState,
	checkHasBaseEntity,
	PropertyState,
	flattenRelatedCanvasElementsOnTheRightAsync,
	flattenRelatedCanvasElementsOnTheLeftAsync,
	flattenCallsAsync,
	resolveEntityName,
	getParentEntryPoint,
	flattenCallerAncestorsUntil,
	getParentExecutableEntity,
	VariableDeclarationState,
	flattenWrites,
	PASS_THROUGH_CALLABLE_TYPES_WITH_OUTPUTS,
	PassThroughCallableEntityWithOutputsState,
	flattenElementCallsWithCallType,
	getRootEntryPointScopeOwner,
	flattenCallers,
	checkEveryCallerReachesOrIsAncestor,
	EntityWithLogicScopeState,
	flattenNodeAsRecord,
	getParentCallableEntity,
	getParentCallerEntity,
	checkIsNestedScope,
	GenerationExplanationIds,
	EntityError,
	applyNewScope,
	flattenRelatedCallableElementsOnTheRight,
	getDeclaration,
	flattenCallersWithCallType,
	StateMutationAction
} from '@elyx-code/project-logic-tree';
import { TextFieldType } from '../../lib/text-field';
import { ConnectionNodeType, EditorService } from '.';
import {
	getCanvasEntityEntryCallerNodeDOMId,
	getCanvasEntityErrorCallerNodeDOMId,
	getCanvasEntityExecutionNodeId,
	getCanvasEntitySuccessCallerNodeDOMId,
	calculateVerticalOffsetForMethod,
	CANVAS_HORIZONTAL_BUFFER_BETWEEN_CARDS,
	CANVAS_CARD_WIDTH
} from './ui';
import { CanvasEntityElement } from './CanvasElement';
import { TestDatabaseDialogMode } from '../../components/test-relational-database/TestDatabaseDialogContent';
import { findNextAvailableName } from './create/utils';
import dayjs from 'dayjs';
import { Logger, YieldOptions, YieldTracker } from '@elyx-code/common-ts-utils';
import { Area } from './area';
import ENTITIES_METADATA_JSON from './entities-metadata.json';

export type DataTypeBuilderOption = DataTypeStateEntityOption | PropertyState;

export function getExternalIntegrationNameFromBuiltInBaseName(
	baseName: BaseEntityNames
): string | null {
	if (!baseName.includes('BUILT_IN_EXTERNAL_INTEGRATION_CONNECTION_')) {
		return null;
	}

	return baseName
		.replace('BUILT_IN_EXTERNAL_INTEGRATION_CONNECTION_', '')
		.toLocaleLowerCase()
		.replace('_auth', '');
}

export function getExternalIntegrationIdFromAction(
	entity: ActionDescriptorState | OperationState
): string | null {
	if (
		entity.type !== EntityType.ActionDescriptor &&
		entity.type !== EntityType.Operation
	) {
		throw new Error(
			'Only ActionDescriptorState or OperationState can be used to resolve an external integration ID'
		);
	}

	if (entity.type === EntityType.ActionDescriptor) {
		const actionDescriptor = entity as ActionDescriptorState;

		if (actionDescriptor.id.startsWith('external-integration--')) {
			return actionDescriptor.id.split('--')[1];
		}
	}

	if (entity.type === EntityType.Operation) {
		const declaration = entity.declaration;

		// If it is an operation that represents an external integration,
		// Return the URL of the icon
		const isExternalIntegration = Object.values(
			ExternalIntegrationOperations
		).includes(declaration.id as ExternalIntegrationOperations);

		if (isExternalIntegration) {
			const integrationNameParts = declaration.id.split('--');

			const integrationName = integrationNameParts[1];

			return integrationName || null;
		}
	}

	return null;
}

export function resolveInputTypeFromLiteralValue(
	value: ILiteralValue
): TextFieldType {
	switch (value?.name) {
		case PrimitiveTypes.Number:
			return 'number';
		case PrimitiveTypes.String:
			return 'text';
		case PrimitiveTypes.Date:
			return 'date';
		default:
			return 'text';
	}

	return 'text';
}

export function resolveInputTypeFromDataType(
	dataType: DataTypeState
): TextFieldType {
	const entity = dataType?.entity;
	const primitiveName =
		entity?.type === EntityType.PrimitiveEntity
			? (entity.name as PrimitiveTypes)
			: null;

	switch (primitiveName) {
		case PrimitiveTypes.Number:
			return 'number';
		case PrimitiveTypes.String:
		case PrimitiveTypes.UUID:
			return 'text';
		case PrimitiveTypes.Date:
			return 'date';
		default:
			return 'text';
	}

	return 'text';
}

// The htpm input fields will always return a string on the onChange event.target.value
// This function will cast the value to the correct type based on the data type
export function castHTMLInputValueToType(
	value: string,
	dataType: DataTypeState
): string | number | Date {
	const entity = dataType?.entity;
	const primitiveName =
		entity?.type === EntityType.PrimitiveEntity
			? (entity.name as PrimitiveTypes)
			: null;

	if (!value) {
		return value;
	}

	switch (primitiveName) {
		case PrimitiveTypes.Number:
			return value;
		case PrimitiveTypes.String:
			return value;
		case PrimitiveTypes.Date:
			return new Date(value).toISOString();
		default:
			return value;
	}

	return value;
}

export function castLiteralValueToHTMLInputValue(
	value: LiteralValueType,
	dataType: DataTypeState
): string {
	const entity = dataType?.entity;
	const primitiveName =
		entity?.type === EntityType.PrimitiveEntity
			? (entity.name as PrimitiveTypes)
			: null;

	if (value === null || value === undefined || value === '') {
		return '';
	}

	switch (primitiveName) {
		case PrimitiveTypes.Number:
			return value.toString();
		case PrimitiveTypes.Date:
			// For date, we need to return only the date part in YYYY-MM-DD format
			// Using dayjs
			return dayjs(value as string).format('YYYY-MM-DD');
		case PrimitiveTypes.Null:
		default:
			return value.toString();
	}
}

export function getAllValueReadingChildren(
	entity: CanvasEntityState | InternalCallState
): ValueReadingEntityState[] {
	const children: ValueReadingEntityState[] = [];

	if (TERMINATION_TYPES.includes(entity.type)) {
		children.push(...(entity as TerminationEntityState).outputs);

		if ((entity as TerminationEntityState).throws) {
			children.push((entity as TerminationEntityState).throws);
		}

		return children;
	}

	if (PASS_THROUGH_CALLABLE_TYPES.includes(entity.type)) {
		children.push(
			...(entity as PassThroughCallableEntityState).inputs.flatMap(
				(input) => [input]
			)
		);

		if (entity.type === EntityType.Condition) {
			children.push(
				...((entity as ConditionState).andChildrenGroup || []).flatMap(
					(condition) => getAllValueReadingChildren(condition)
				),
				...((entity as ConditionState).orChildrenGroup || []).flatMap(
					(condition) => getAllValueReadingChildren(condition)
				)
			);
		}

		if (VARIABLE_TYPES.includes(entity.type)) {
			children.push(
				...(entity as VariableState).internalCalls.flatMap(
					(internalCall) => getAllValueReadingChildren(internalCall)
				)
			);

			children.push(entity as VariableState);
		}

		return filterOutDuplicateEntities(
			children
		) as ValueReadingEntityState[];
	}

	return [];
}

export function getAllValueWritingChildren(
	entity: CanvasEntityState | InternalCallState
): ValueWritingEntityState[] {
	const children: ValueWritingEntityState[] = [];

	if (
		PASS_THROUGH_CALLABLE_TYPES.includes(entity.type) &&
		entity.type !== EntityType.Condition
	) {
		children.push(entity as PassThroughCallableEntityWithOutputsState);
		children.push(
			...(entity as PassThroughCallableEntityWithOutputsState).outputs
		);

		if (VARIABLE_TYPES.includes(entity.type)) {
			children.push(
				...(entity as VariableState).internalCalls.flatMap(
					(internalCall) => getAllValueWritingChildren(internalCall)
				)
			);

			children.push(entity as VariableState);
		}

		return children;
	}

	if (ENTRY_POINT_TYPES.includes(entity.type)) {
		children.push(...(entity as EntryPointEntityState).inputs);

		return children;
	}

	return [];
}

export async function getAllValueWritingEntities(
	project: ProjectState,
	options: YieldOptions = {}
): Promise<ValueWritingEntityState[]> {
	const tracker = YieldTracker.from(options);

	const allValueWritingEntities: ValueWritingEntityState[] = [];

	for (const ent of project.instancesList) {
		if (VALUE_WRITING_TYPES.includes(ent.type)) {
			allValueWritingEntities.push(ent as ValueWritingEntityState);
		}

		await tracker.tick();
	}

	return allValueWritingEntities;
}

export async function getAllGlobalValueWritingEntities(
	project: ProjectState,
	options: YieldOptions = {}
): Promise<ValueWritingEntityState[]> {
	const tracker = YieldTracker.from(options);

	const allValueWritingEntities: ValueWritingEntityState[] = [];

	for (const ent of project.instancesList) {
		if (VALUE_WRITING_TYPES.includes(ent.type)) {
			const canvasParent = getParentCanvasEntity(ent);

			if (
				canvasParent?.parent === project &&
				canvasParent.type !== EntityType.DefinitionEntity &&
				canvasParent.type !== EntityType.InstalledProject
			) {
				allValueWritingEntities.push(ent as ValueWritingEntityState);
			}
		}

		await tracker.tick();
	}

	return allValueWritingEntities;
}

export async function getAllGlobalValueReadingEntities(
	project: ProjectState,
	options: YieldOptions = {}
): Promise<ValueReadingEntityState[]> {
	const tracker = YieldTracker.from(options);

	const allValueReadingEntities: ValueReadingEntityState[] = [];

	for (const ent of project.instancesList) {
		if (VALUE_READING_TYPES.includes(ent.type)) {
			const canvasParent = getParentCanvasEntity(ent);

			if (
				canvasParent?.parent === project &&
				canvasParent.type !== EntityType.DefinitionEntity &&
				canvasParent.type !== EntityType.InstalledProject
			) {
				allValueReadingEntities.push(ent as ValueReadingEntityState);
			}
		}

		await tracker.tick();
	}

	return allValueReadingEntities;
}

export interface ICompatibleConnectionOption {
	requiresCallConnectionTo: CallableEntityState | null;
	requiresCallConnectionToFrom: CallerEntityState | null;
	entity: UserManagedEntityState;
}

export interface IConnectionCompatibleOptions {
	[id: EntityId]: ICompatibleConnectionOption;
}

export interface IIncompatibleConnectionOption {
	entity: UserManagedEntityState;
	reasons: CanvasEntityConnectionDisabledReason[];
	fixable: boolean;
	requiresCallConnectionTo: CallableEntityState | null;
	requiresCallConnectionToFrom: CallerEntityState | null;
	nonAutofixableReasons: (
		| CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
		| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
		| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
	)[];
}

export interface IConnectionIncompatibleOptions {
	[id: EntityId]: IIncompatibleConnectionOption;
}

export interface IConnectionCompatibilityResult {
	compatible: IConnectionCompatibleOptions;
	incompatible: IConnectionIncompatibleOptions;
}

export function moveParallelCalls(
	entity: PassThroughCallableEntityState,
	changeSet: ChangeSet,
	options?: {
		onlyTypes?: EntityType[];
		onConnection?: (
			sourceEntity: UserManagedEntityState,
			targetEntity: UserManagedEntityState,
			connectionType: ConnectionNodeType
		) => Promise<void>;
		onCallRemoved?: (
			caller: CallerEntityState,
			callee: CallableEntityState,
			callType: 'success' | 'error' | 'entry' | 'loop-body'
		) => Promise<void>;
	}
): {
	errors: EntityError[];
	explanations: { id: GenerationExplanationIds; message: string }[];
} {
	const optionsWithDefaults = {
		onConnection: async () => {},
		onCallRemoved: async () => {},
		onlyTypes: CALLABLE_TYPES,
		...options
	};

	const explanations: { id: GenerationExplanationIds; message: string }[] =
		[];
	const errors: EntityError[] = [];

	if (!PASS_THROUGH_CALLABLE_TYPES.includes(entity.type)) {
		return { explanations, errors };
	}

	// For each caller, check if they call any callee points (return, break, continue)
	// If they do, make sure the new entity is also calling those callee points, racing conditions (eg: being called after return statement)
	// Then remove the call between the caller and the callee point
	entity.calledBySuccess.forEach((caller) => {
		const relevantCallees = caller.successCalls.filter(
			(calledEnt) =>
				optionsWithDefaults.onlyTypes.includes(calledEnt.type) &&
				calledEnt.id !== entity.id
		);

		relevantCallees.forEach((callee) => {
			if (!TERMINATION_TYPES.includes(callee.type)) {
				const relatedEntitiesOnTheRight =
					flattenRelatedCallableElementsOnTheRight(callee);

				if (
					relatedEntitiesOnTheRight.some(
						(ent) => ent.id === entity.id
					)
				) {
					return;
				}
			}

			entity.addSuccessCall(callee, changeSet);
			callee.addSuccessCaller(entity, changeSet, true);
			applyNewScope(entity, changeSet);

			optionsWithDefaults.onConnection?.(
				entity,
				callee,
				ConnectionNodeType.CalledBy
			);

			caller.removeCall(callee, changeSet);
			callee.removeCaller(caller, changeSet);

			optionsWithDefaults.onCallRemoved?.(caller, callee, 'success');
		});
	});

	entity.calledByError.forEach((caller) => {
		const relevantCallees = caller.errorCalls.filter(
			(calledEnt) =>
				optionsWithDefaults.onlyTypes.includes(calledEnt.type) &&
				calledEnt.id !== entity.id
		);

		relevantCallees.forEach((callee) => {
			if (!TERMINATION_TYPES.includes(callee.type)) {
				const relatedEntitiesOnTheRight =
					flattenRelatedCallableElementsOnTheRight(callee);

				if (
					relatedEntitiesOnTheRight.some(
						(ent) => ent.id === entity.id
					)
				) {
					return;
				}
			}

			entity.addErrorCall(callee, changeSet);
			callee.addErrorCaller(entity, changeSet, true);
			applyNewScope(entity, changeSet);

			optionsWithDefaults.onConnection?.(
				entity,
				callee,
				ConnectionNodeType.CalledBy
			);

			caller.removeCall(callee, changeSet);
			callee.removeCaller(caller, changeSet);

			optionsWithDefaults.onCallRemoved?.(caller, callee, 'error');
		});
	});

	entity.calledByEntry.forEach((caller) => {
		const relevantCallees = caller.calls.filter(
			(calledEnt) =>
				optionsWithDefaults.onlyTypes.includes(calledEnt.type) &&
				calledEnt.id !== entity.id
		);

		relevantCallees.forEach((callee) => {
			if (!TERMINATION_TYPES.includes(callee.type)) {
				const relatedEntitiesOnTheRight =
					flattenRelatedCallableElementsOnTheRight(callee);

				if (
					relatedEntitiesOnTheRight.some(
						(ent) => ent.id === entity.id
					)
				) {
					return;
				}
			}

			entity.addSuccessCall(callee, changeSet);
			callee.addSuccessCaller(entity, changeSet, true);
			applyNewScope(entity, changeSet);

			optionsWithDefaults.onConnection?.(
				entity,
				callee,
				ConnectionNodeType.CalledBy
			);

			caller.removeCall(callee, changeSet);
			callee.removeCaller(caller, changeSet);

			optionsWithDefaults.onCallRemoved?.(caller, callee, 'entry');
		});
	});

	return { explanations, errors };
}

/**
 * Returns a list of all entities that the current entity can connect to and not connect to
 *
 * @param {ArgumentDeclarationState | InputMapState | OutputMapState | LoopState | CallerEntityState | VariableState | ValueDescriptorState | ExecutableEntityState} entity The entity for which we want to find compatible and incompatible connections
 * @param {ConnectionNodeType} connectionType The type of connection we want to create
 * @param {YieldOptions & { allowReadWriteToFixableScope?: boolean }} options Additional options for the function, including yield options and whether to allow connections to entities that could become compatible if the scope is changed
 *
 * @returns {Promise<IConnectionCompatibilityResult>} An object containing two lists: one for compatible entities and one for incompatible entities, each with the reasons for their compatibility or incompatibility
 */
export async function getIncompatibleConnectionEntitiesAsync(
	entity:
		| ArgumentDeclarationState
		| InputMapState
		| OutputMapState
		| LoopState
		| CallerEntityState
		| VariableState
		| ValueDescriptorState
		| ExecutableEntityState,
	connectionType: ConnectionNodeType,
	options: YieldOptions & { allowReadWriteToFixableScope?: boolean } = {}
): Promise<IConnectionCompatibilityResult> {
	const tracker = YieldTracker.from(options);

	const compatibleEntities: IConnectionCompatibleOptions = {};

	const incompatibleEntities: IConnectionIncompatibleOptions = {};

	const compatibleIfScopeChange: {
		[id: string]: {
			entity: UserManagedEntityState;
			requiresCallConnectionTo: CallableEntityState;
			requiresCallConnectionToFrom: CallerEntityState;
		};
	} = {};

	const incompatibleButFixableWithScopeChange: {
		[id: string]: {
			entity: UserManagedEntityState;
			requiresCallConnectionTo: CallableEntityState;
			requiresCallConnectionToFrom: CallerEntityState;
			reasons: (
				| CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
				| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
				| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
			)[];
		};
	} = {};

	const parentCanvasEntity = getParentCanvasEntity(
		entity
	) as CanvasEntityState;

	const flattenedRelatedEntitiesOnTheRight =
		await flattenRelatedCanvasElementsOnTheRightAsync(
			parentCanvasEntity,
			false,
			{ tracker }
		);
	const flattenedRelatedEntitiesOnTheLeft =
		await flattenRelatedCanvasElementsOnTheLeftAsync(parentCanvasEntity, {
			tracker
		});

	// Local helper functions to add and remove entities and reasons to the compatible and incompatible lists
	const addEntityToCompatibleList = (entity: UserManagedEntityState) => {
		compatibleEntities[entity.id] = {
			entity,
			requiresCallConnectionTo:
				compatibleIfScopeChange[entity.id]?.requiresCallConnectionTo ||
				null,
			requiresCallConnectionToFrom:
				compatibleIfScopeChange[entity.id]
					?.requiresCallConnectionToFrom || null
		};
	};

	const addEntityToIncompatibleList = (
		ent: UserManagedEntityState,
		reasons: [
			CanvasEntityConnectionDisabledReason,
			...CanvasEntityConnectionDisabledReason[]
		]
	) => {
		const aggregatedReasons = mergeArraysOfStringsWithoutDuplicates(
			incompatibleEntities[ent.id]?.reasons || [],
			reasons
		) as CanvasEntityConnectionDisabledReason[];

		const isOnlyOutOfScopeReason = aggregatedReasons.every(
			(reason) =>
				CanvasEntityConnectionDisabledReason.NotInScope === reason
		);

		if (isOnlyOutOfScopeReason && compatibleIfScopeChange[ent.id]) {
			compatibleEntities[ent.id] = {
				entity: ent,
				requiresCallConnectionTo:
					compatibleIfScopeChange[ent.id].requiresCallConnectionTo,
				requiresCallConnectionToFrom:
					compatibleIfScopeChange[ent.id].requiresCallConnectionToFrom
			};
			delete incompatibleEntities[ent.id];
		} else {
			incompatibleEntities[ent.id] = {
				entity: ent,
				reasons: aggregatedReasons,
				fixable: !!(
					incompatibleButFixableWithScopeChange[ent.id]
						?.requiresCallConnectionTo &&
					incompatibleButFixableWithScopeChange[ent.id]
						?.requiresCallConnectionToFrom
				),
				requiresCallConnectionTo:
					incompatibleButFixableWithScopeChange[ent.id]
						?.requiresCallConnectionTo || null,
				requiresCallConnectionToFrom:
					incompatibleButFixableWithScopeChange[ent.id]
						?.requiresCallConnectionToFrom || null,
				nonAutofixableReasons:
					incompatibleButFixableWithScopeChange[ent.id]?.reasons || []
			};
		}
	};

	const addEntitiesToIncompatibleListAsync = async (
		entities: UserManagedEntityState[],
		reasons: [
			CanvasEntityConnectionDisabledReason,
			...CanvasEntityConnectionDisabledReason[]
		]
	) => {
		for (const entity of entities) {
			addEntityToIncompatibleList(entity, reasons);

			await tracker.tick();
		}
	};

	addEntityToIncompatibleList(entity, [
		CanvasEntityConnectionDisabledReason.CannotConnectToItself
	]);

	const ownNodeRecord = flattenNodeAsRecord(
		parentCanvasEntity,
		entity.project.instances
	) as Record<EntityId, UserManagedEntityState>;

	Object.values(ownNodeRecord).forEach((node) => {
		if (node.id === entity.id) {
			return;
		}

		addEntityToIncompatibleList(node, [
			CanvasEntityConnectionDisabledReason.CannotConnectToOwnNode
		]);
	});

	// For all connection types that go from right to left, all entities on the right should be disabled.
	// For all connection types that go from left to right, all entities on the left should be disabled.

	// Connection goes right to left (backwards)
	if (
		[
			ConnectionNodeType.CalledBy,
			ConnectionNodeType.CalledByError
		].includes(connectionType)
	) {
		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheRight,
			[CanvasEntityConnectionDisabledReason.CannotBeCalledByChildren]
		);

		const ownFlatCallers = flattenCallers(entity as CallableEntityState);

		ownFlatCallers.forEach((caller) => {
			addEntityToIncompatibleList(caller, [
				CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
			]);
		});

		const ownFlatCalls = PASS_THROUGH_CALLABLE_TYPES.includes(entity.type)
			? flattenElementCallsWithCallType(
					entity as PassThroughCallableEntityState
				)
			: [];

		ownFlatCalls.forEach((call) => {
			addEntityToIncompatibleList(call.callee, [
				CanvasEntityConnectionDisabledReason.CannotBeCalledByChildren
			]);
		});

		let localRelatedEntitiesOnTheLeft: (
			| CanvasEntityState
			| InternalCallState
		)[] = flattenedRelatedEntitiesOnTheLeft;

		const allVariablesOnTheLeft = flattenedRelatedEntitiesOnTheLeft.filter(
			(ent) => VARIABLE_TYPES.includes(ent.type)
		) as VariableState[];

		const allVariablesWithoutValueSetting = allVariablesOnTheLeft.filter(
			(ent) =>
				!ent.readsValue &&
				!ent.writesValues.length &&
				!ent.defaultValue &&
				!ent.inputs.some((input) => !!input.readsValue) &&
				!ent.outputs.some((output) => !!output.writesValues.length) &&
				// If it is an instance or a variable declaration that isn't globally declared
				(ent.type !== EntityType.VariableDeclaration ||
					ent.parent.type !== EntityType.Project)
		);

		const allAvailableInternalCalls = allVariablesWithoutValueSetting
			.flatMap((ent) => ent.internalCalls)
			.filter((internalCall) => internalCall.id !== entity.id);

		allAvailableInternalCalls.forEach((internalCall) => {
			// If the internal call is already called by the entity, we should add it to the incompatible list
			if (
				(internalCall as CallableEntityState).calledBy.includes(
					entity as CallerEntityState
				)
			) {
				addEntityToIncompatibleList(internalCall, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
			}

			// if the internal call is part of a variable in which another internal call is being executed,
			// we should add it to the incompatible list
			const parentVar = internalCall.parent;
			const otherInternalCallsOfParent =
				parentVar.usedInternalCalls.filter(
					(call) => call.id !== entity.id
				);

			if (otherInternalCallsOfParent.length) {
				addEntityToIncompatibleList(internalCall, [
					CanvasEntityConnectionDisabledReason.SiblingInternalCallAlreadyUsedInParentVariable
				]);
			}
		});

		localRelatedEntitiesOnTheLeft.push(...allAvailableInternalCalls);

		const rootEntryPointScopeOwner = getRootEntryPointScopeOwner(
			entity,
			entity.project
		);

		const ownScopeOwner = getScopeOwner(entity, entity.project) as
			| EntityWithLogicScopeState
			| ProjectState;

		const flattenedRootEntryPointCallers = !!rootEntryPointScopeOwner
			? flattenElementCalls(rootEntryPointScopeOwner).filter(
					(call) =>
						call.id !== entity.id &&
						ownFlatCalls.every(
							(ownCall) => ownCall.callee.id !== call.id
						) &&
						CALLER_TYPES.includes(call.type) &&
						// (!PASS_THROUGH_CALLABLE_TYPES.includes(call.type) ||
						// (!(call as PassThroughCallableEntityState).successCalls.length &&
						// 	!(call as PassThroughCallableEntityState).errorCalls.length)) &&
						!localRelatedEntitiesOnTheLeft.some(
							(ent) => ent.id === call.id
						) &&
						getScopeOwner(call, entity.project)?.id ===
							ownScopeOwner?.id
				)
			: [];

		localRelatedEntitiesOnTheLeft.push(...flattenedRootEntryPointCallers);

		localRelatedEntitiesOnTheLeft = filterOutDuplicateEntities(
			localRelatedEntitiesOnTheLeft
		) as CallerEntityState[];

		// If the two entities are already, we should add the entity to the incompatible list
		for (const caller of localRelatedEntitiesOnTheLeft) {
			await tracker.tick();

			if (
				(entity as CallableEntityState).calledBy.includes(
					caller as CallerEntityState
				)
			) {
				addEntityToIncompatibleList(caller, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
				continue;
			}

			const callerScope = getScopeOwner(caller, entity.project);

			if (
				(entity.type === EntityType.ContinueStatement ||
					entity.type === EntityType.BreakStatement) &&
				callerScope?.type !== EntityType.Loop
			) {
				addEntityToIncompatibleList(caller, [
					CanvasEntityConnectionDisabledReason.ContinueOrBreakStatementCalledFromOutsideLoop
				]);
				continue;
			}

			const scopeCompatibility = checkScopeCompatibility(
				entity,
				caller,
				entity.project
			);

			if (scopeCompatibility.type === ScopeCompatibilityType.AInB) {
				addEntityToIncompatibleList(caller, [
					CanvasEntityConnectionDisabledReason.CallerIsInHigherScope
				]);
			} else if (!scopeCompatibility.compatible) {
				addEntityToIncompatibleList(caller, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
			} else {
				if (
					connectionType === ConnectionNodeType.CalledByError &&
					!PASS_THROUGH_CALLABLE_TYPES.includes(caller.type)
				) {
					addEntityToIncompatibleList(caller, [
						CanvasEntityConnectionDisabledReason.CalledByErrorMustBePassThroughCallable
					]);
				}

				if (!CALLER_TYPES.includes(caller.type)) {
					addEntityToIncompatibleList(caller, [
						CanvasEntityConnectionDisabledReason.CalledBySourceMustBeCallerEntity
					]);
				}

				const resultAlreadyConnected = flattenCallerAncestorsUntil(
					entity as CallableEntityState,
					caller as CallerEntityState
				);

				if (resultAlreadyConnected.last?.id === caller.id) {
					addEntityToIncompatibleList(caller, [
						CanvasEntityConnectionDisabledReason.RedundantCall
					]);
				}

				// If the connection would make an 'internal-call' or 'variable-instance'
				// be called before or in parallel to its own parent variable-declaration, we should add it to the incompatible list
				// It doesn't matter whether the caller is actually an 'internal-call' or a 'variable-instance'
				// Because based on a new call being established, a new conflict somewhere else could be created
				if (PASS_THROUGH_CALLABLE_TYPES.includes(entity.type)) {
					const allCalledVariableDeclarationsCalled =
						ownFlatCalls.filter(
							(flattened) =>
								flattened.callee.type ===
								EntityType.VariableDeclaration
						);

					if (entity.type === EntityType.VariableDeclaration) {
						allCalledVariableDeclarationsCalled.push({
							callee: entity,
							callType: 'success'
						});
					}

					for (const calledVarDecl of allCalledVariableDeclarationsCalled) {
						await tracker.tick();
						const varDecl =
							calledVarDecl.callee as VariableDeclarationState;

						if (caller.type === EntityType.VariableInstance) {
							const callerVarDecl = caller.declaration;

							if (callerVarDecl?.id === varDecl.id) {
								addEntityToIncompatibleList(caller, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
								break;
							}
						} else if (caller.type === EntityType.InternalCall) {
							const callerVarDecl = getDeclaration(caller.parent);

							if (callerVarDecl?.id === varDecl.id) {
								addEntityToIncompatibleList(caller, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
								break;
							}
						}

						if (
							!!ownScopeOwner &&
							ownScopeOwner.type !== EntityType.Project
						) {
							let flatCallersUntilNextScope =
								flattenCallerAncestorsUntil(
									entity as CallableEntityState,
									ownScopeOwner as CallerEntityState
								);

							// Recursively make sure non of the callers until the next scope owner
							// Are a 'variable-instance' or an 'internal-call' of the current 'variable-declaration'
							while (flatCallersUntilNextScope.last) {
								await tracker.tick();
								const someAreVariableInstancesOrInternalCalls =
									flatCallersUntilNextScope.list.some(
										(flattened) =>
											(flattened.type ===
												EntityType.VariableInstance &&
												flattened.declaration?.id ===
													varDecl.id) ||
											(flattened.type ===
												EntityType.InternalCall &&
												getDeclaration(flattened.parent)
													?.id === varDecl?.id)
									);

								if (someAreVariableInstancesOrInternalCalls) {
									addEntityToIncompatibleList(caller, [
										CanvasEntityConnectionDisabledReason.WouldMakeAnEarlierNodeBeCalledBeforeItsDeclaration
									]);

									break;
								}

								const nextScopeOwner = getScopeOwner(
									flatCallersUntilNextScope.last,
									entity.project
								);

								if (
									nextScopeOwner?.id ===
									flatCallersUntilNextScope.last?.id
								) {
									break;
								}

								if (
									!!nextScopeOwner &&
									nextScopeOwner.type !== EntityType.Project
								) {
									flatCallersUntilNextScope =
										flattenCallerAncestorsUntil(
											entity as CallableEntityState,
											nextScopeOwner as CallerEntityState
										);
								} else if (
									nextScopeOwner?.type === EntityType.Project
								) {
									const allCallers = flattenCallers(
										entity as CallableEntityState
									);

									const someCallerIsVariableInstanceOrInternalCall =
										allCallers.some(
											(flattened) =>
												(flattened.type ===
													EntityType.VariableInstance &&
													flattened.declaration
														?.id === varDecl.id) ||
												(flattened.type ===
													EntityType.InternalCall &&
													getDeclaration(
														flattened.parent
													)?.id === varDecl?.id)
										);

									if (
										someCallerIsVariableInstanceOrInternalCall
									) {
										addEntityToIncompatibleList(caller, [
											CanvasEntityConnectionDisabledReason.WouldMakeAnEarlierNodeBeCalledBeforeItsDeclaration
										]);
									}
									break;
								}
							}
						} else if (ownScopeOwner?.type === EntityType.Project) {
							const allCallers = flattenCallers(
								entity as CallableEntityState
							);

							const someCallerIsVariableInstanceOrInternalCall =
								allCallers.some(
									(flattened) =>
										(flattened.type ===
											EntityType.VariableInstance &&
											flattened.declaration?.id ===
												varDecl.id) ||
										(flattened.type ===
											EntityType.InternalCall &&
											getDeclaration(flattened.parent)
												?.id === varDecl?.id)
								);

							if (someCallerIsVariableInstanceOrInternalCall) {
								addEntityToIncompatibleList(caller, [
									CanvasEntityConnectionDisabledReason.WouldMakeAnEarlierNodeBeCalledBeforeItsDeclaration
								]);

								continue;
							}
						}
					}
				}

				if (PASS_THROUGH_CALLABLE_TYPES.includes(caller.type)) {
					// Here check if any of the branches of the caller should be incompatible
					// If the current entity is called by a success branch of another caller,
					// Then it cannot be connected to the error branch of a separate caller.
					// An entity can only be connected to an error branch if it is then only caller,
					// or if all other callers are also desdendants of the same error branch.
					const callerCallers = flattenCallers(
						caller as PassThroughCallableEntityState
					);

					if (
						ownFlatCallers.some((ownCaller) => {
							return !callerCallers.find(
								(callerCaller) =>
									callerCaller.id === ownCaller.id
							);
						})
					) {
						addEntityToIncompatibleList(caller, [
							('error-caller-only--' +
								CanvasEntityConnectionDisabledReason.EntityAlreadyCalledBySuccessCannotBeConnectedToUnrelatedErrorBranch) as CanvasEntityConnectionDisabledReason
						]);
					}
				}

				// Check that the caller doesn't go beyong an initial error call of the current entity
				const callerAncestors = flattenCallersWithCallType(
					entity as CallableEntityState
				);

				const calledByExistingError = callerAncestors.find(
					(c) => c.callType === 'error'
				);

				if (!!calledByExistingError) {
					const existingCallersOnSameErrorBranch =
						flattenCallerAncestorsUntil(
							entity as CallableEntityState,
							calledByExistingError.caller
						);

					const isWithinErrorSameBranch =
						!!existingCallersOnSameErrorBranch.list.find(
							(c) => c.id === caller.id
						);

					if (!isWithinErrorSameBranch) {
						addEntityToIncompatibleList(caller, [
							CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
						]);
					}
				}

				if (
					!compatibleEntities[caller.id] &&
					!incompatibleEntities[caller.id]
				) {
					addEntityToCompatibleList(caller);
				}
			}
		}

		(ownScopeOwner?.type === EntityType.Project
			? []
			: flattenElementCalls(ownScopeOwner)
		).forEach((callee) => {
			if (callee.id === entity.id) {
				return false;
			}

			if (
				flattenedRelatedEntitiesOnTheRight.includes(
					callee as CanvasEntityState
				)
			) {
				return false;
			}

			// For every callee of the entry-point,
			// we want check that their branches are compatible
			// If the current callee calls the current entity on success, then all entities it calls on error should be incompatible.
			// And if the current callee calls the current entity on error, then all entities it calls on success should be incompatible.
			if (CALLER_TYPES.includes(callee.type)) {
				const calleeCalls = flattenElementCallsWithCallType(
					callee as CallerEntityState
				);

				const callsCurrentEntityOnSuccess = calleeCalls.find(
					(flattened) =>
						flattened.callee.id === entity.id &&
						flattened.callType === 'success'
				);

				const callsCurrentEntityOnError = calleeCalls.find(
					(flattened) =>
						flattened.callee.id === entity.id &&
						flattened.callType === 'error'
				);

				if (!!callsCurrentEntityOnSuccess) {
					calleeCalls.forEach((call) => {
						if (call.callType === 'error') {
							addEntityToIncompatibleList(call.callee, [
								CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
							]);
						}
					});
				}

				if (!!callsCurrentEntityOnError) {
					calleeCalls.forEach((call) => {
						if (call.callType === 'success') {
							addEntityToIncompatibleList(call.callee, [
								CanvasEntityConnectionDisabledReason.CalledBySuccessCannotBeConnectedToEntitiesCalledByOwnErrorBranch
							]);
						}
					});
				}
			}

			if (
				localRelatedEntitiesOnTheLeft.includes(
					callee as CanvasEntityState
				)
			) {
				return false;
			}

			const targetScopeOwner = getScopeOwner(
				callee as CanvasEntityState,
				entity.project
			);

			if (
				checkIsBranchDependentButNotDirectlyOnBranch(
					entity as CanvasEntityState,
					callee as CanvasEntityState
				)
			) {
				if (
					!compatibleEntities[callee.id] &&
					!incompatibleEntities[callee.id]
				) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CannotBeCalledByChildren
					]);
				}
			} else if (targetScopeOwner !== ownScopeOwner) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
			} else {
				if (
					connectionType === ConnectionNodeType.CalledByError &&
					!PASS_THROUGH_CALLABLE_TYPES.includes(callee.type)
				) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CalledByErrorMustBePassThroughCallable
					]);
				}

				if (!CALLER_TYPES.includes(callee.type)) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CalledBySourceMustBeCallerEntity
					]);
				}

				if (
					!compatibleEntities[callee.id] &&
					!incompatibleEntities[callee.id]
				) {
					addEntityToCompatibleList(callee);
				}
			}
		});

		// If the endity is global, all other globals can be connected
		if (entity.parent === entity.project) {
			const allCallers = entity.project.instancesList.filter(
				(ent) => ent.id !== entity.id && CALLER_TYPES.includes(ent.type)
			) as UserManagedEntityState[];

			allCallers.forEach((caller) => {
				if (
					connectionType === ConnectionNodeType.CalledByError &&
					!PASS_THROUGH_CALLABLE_TYPES.includes(caller.type)
				) {
					addEntityToIncompatibleList(caller, [
						CanvasEntityConnectionDisabledReason.CalledByErrorMustBePassThroughCallable
					]);
				}

				if (
					!compatibleEntities[caller.id] &&
					!incompatibleEntities[caller.id]
				) {
					addEntityToCompatibleList(caller as UserManagedEntityState);
				}
			});
		}
	} else if ([ConnectionNodeType.PropertyIn].includes(connectionType)) {
		console.log(ConnectionNodeType.PropertyIn);
		const parentCallableEntity = getParentCallableEntity(
			entity
		) as CallableEntityState;

		// We follow the callstack and if we find any ancestors that call the current in its error branch,
		// It means we are in an error branch's scope
		// Therefore we shouldn't have access to the success output values from that node
		// But we can have access to the ones from previous nodes
		const errorCaller = flattenCallersWithCallType(
			parentCallableEntity
		).filter((a) => a.callType === 'error');

		if (!!errorCaller.length) {
			errorCaller.forEach((c) => {
				const valueWrittingChildren = getAllValueWritingChildren(
					c.caller as InternalCallState | CanvasEntityState
				);

				// Only error outputs can be connected to the error branch
				valueWrittingChildren.forEach((vwc) => {
					if (
						vwc.type === EntityType.OutputMap &&
						vwc.parentRelationType ===
							OutputMapParentChildRelation.Error
					) {
						return;
					}

					addEntityToIncompatibleList(vwc, [
						CanvasEntityConnectionDisabledReason.SuccessOutputMapCannotBeConnectedToParentErrorBranchCallees
					]);
				});
			});
		}

		const ownNodeRecord = flattenNodeAsRecord(
			parentCanvasEntity,
			entity.project.instances
		) as Record<EntityId, UserManagedEntityState>;

		const ownCalls = PASS_THROUGH_CALLABLE_TYPES.includes(entity.type)
			? flattenElementCalls(entity as PassThroughCallableEntityState)
			: [];

		Object.values(ownNodeRecord).forEach((node) => {
			if (node.id === entity.id) {
				return;
			}

			addEntityToIncompatibleList(node, [
				CanvasEntityConnectionDisabledReason.CannotConnectToOwnNode
			]);
		});

		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheRight,
			[CanvasEntityConnectionDisabledReason.CannotReadValueFromChildren]
		);

		const allValueWritingEntities =
			flattenedRelatedEntitiesOnTheLeft.flatMap((entity) =>
				getAllValueWritingChildren(entity as CanvasEntityState)
			);

		const ownScopeOwner = getScopeOwner(entity, entity.project);
		const currentIsGlobal = ownScopeOwner?.type === EntityType.Project;

		let isGlobalAndCanGoLowerScope = false;

		if (
			currentIsGlobal &&
			EntityType.VariableDeclaration !== parentCanvasEntity.type &&
			!parentCallableEntity.calledBy.length
		) {
			isGlobalAndCanGoLowerScope = true;
		}

		// There might be entities in a global scope that if connected, would be compatible.
		// If the 'allowReadWriteToFixableScope' option is enabled, we want to allow connections to those entities,
		// We should gather here all value writing entities on a global scope.
		// If they aren't yet called-by anything, we directly add them to 'compatible' with 'requiresCallConnectionFrom' as itself, and the 'requiresCallConnectionTo' field pointing to the current canvas parent entity.
		// if they are already called by others, we add them to incompatible but add the first entity in their call-stack as the 'requiresCallConnectionFrom' and the current canvas parent entity as the 'requiresCallConnectionTo'
		if (options.allowReadWriteToFixableScope) {
			const allPotentiallyScopeCompatibleValueWritingEntities = [
				...(isGlobalAndCanGoLowerScope
					? await getAllValueWritingEntities(entity.project, {
							tracker
						})
					: await getAllGlobalValueWritingEntities(entity.project, {
							tracker
						})),
				...(!!ownScopeOwner &&
				ownScopeOwner?.type !== EntityType.Project
					? flattenElementCalls(ownScopeOwner)
							.filter(
								(call) =>
									call.id !== entity.id &&
									!ownCalls.find(
										(ownCall) => ownCall.id === call.id
									)
							)
							.flatMap((call) => getAllValueWritingChildren(call))
					: [])
			];

			allPotentiallyScopeCompatibleValueWritingEntities.forEach(
				(writer) => {
					if (allValueWritingEntities.includes(writer)) {
						return;
					}

					const writerParentExecutableNode =
						getParentExecutableEntity(writer);
					const potentialNewScopeOwner = getScopeOwner(
						writer,
						entity.project
					);

					const cantChangeScopeReasons: CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage[] =
						[];

					if (
						!!potentialNewScopeOwner &&
						parentCallableEntity.type ===
							EntityType.VariableDeclaration &&
						!parentCallableEntity.variableInstances.every(
							(varInst) => {
								const instanceScopeOwner = getScopeOwner(
									varInst,
									entity.project
								);

								const compatibleWithNewScope =
									!!instanceScopeOwner &&
									(checkIsNestedScope(
										potentialNewScopeOwner,
										instanceScopeOwner,
										entity.project
									) ||
										potentialNewScopeOwner?.id ===
											instanceScopeOwner?.id);

								return compatibleWithNewScope;
							}
						)
					) {
						cantChangeScopeReasons.push(
							CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
						);
					}

					if (
						!!parentCallableEntity &&
						!!writerParentExecutableNode
					) {
						const parentCallableAggCalls = [
							...((
								parentCallableEntity as PassThroughCallableEntityState
							).successCalls || []),
							...((
								parentCallableEntity as PassThroughCallableEntityState
							).errorCalls || [])
						];

						const alreadyCalledByOtherEntitiesInDifferentScope =
							parentCallableEntity.calledBy.some(
								(caller) =>
									getScopeOwner(caller, entity.project)
										?.id !== potentialNewScopeOwner?.id
							);

						if (
							!alreadyCalledByOtherEntitiesInDifferentScope &&
							parentCallableAggCalls.length === 0 &&
							!cantChangeScopeReasons.length
						) {
							compatibleIfScopeChange[writer.id] = {
								entity: writer,
								requiresCallConnectionTo: parentCallableEntity,
								requiresCallConnectionToFrom:
									writerParentExecutableNode as CallerEntityState
							};
							allValueWritingEntities.push(writer);
						} else {
							incompatibleButFixableWithScopeChange[writer.id] = {
								entity: writer,
								requiresCallConnectionTo: parentCallableEntity,
								requiresCallConnectionToFrom:
									writerParentExecutableNode as CallerEntityState,
								reasons: [
									...(parentCallableAggCalls.length
										? [
												CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
											]
										: []),
									...(alreadyCalledByOtherEntitiesInDifferentScope
										? [
												CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
											]
										: []),
									...cantChangeScopeReasons
								] as (
									| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
									| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
									| CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
								)[]
							};
						}
					}
				}
			);
		}

		// If the two entities are already, we should add the entity to the incompatible list
		allValueWritingEntities.forEach((writer) => {
			const writerDataType = writer.getDataType(null);
			const readerDataType = (
				entity as ValueReadingEntityState
			).getDataType(null);

			if ((entity as ValueReadingEntityState).readsValue === writer) {
				addEntityToIncompatibleList(writer, [
					CanvasEntityConnectionDisabledReason.ValuesAlreadyConnected
				]);
				return;
			} else if (
				!checkAreDataTypesCompatible(readerDataType, writerDataType)
					.compatible
			) {
				addEntityToIncompatibleList(writer, [
					CanvasEntityConnectionDisabledReason.DataTypesNotCompatible
				]);
				return;
			}

			if (
				writer.type === EntityType.OutputMap &&
				(writer.parent.type === EntityType.VariableDeclaration ||
					writer.parent.type === EntityType.VariableInstance) &&
				writer.parent.internalCalls.some(
					(call) => !!call.calledBy.length
				) &&
				// We check this, because it might have been added as "compatibleIfScopeChange" before
				!compatibleEntities[writer.id]
			) {
				addEntityToIncompatibleList(writer, [
					CanvasEntityConnectionDisabledReason.CannotInteractWithOutputInVariableWithInternalCalls
				]);
			}

			if (!incompatibleEntities[writer.id]) {
				addEntityToCompatibleList(writer);
			}
		});

		flattenedRelatedEntitiesOnTheLeft.forEach((ent) => {
			if (!compatibleEntities[ent.id] && !incompatibleEntities[ent.id]) {
				if (!VALUE_WRITING_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotValueWritingEntity
					]);
				}
			}
		});
	} else if ([ConnectionNodeType.EntryPointCaller].includes(connectionType)) {
		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheLeft,
			[CanvasEntityConnectionDisabledReason.CannotCallAncestor]
		);

		// If the two entities are already, we should add the entity to the incompatible list
		flattenedRelatedEntitiesOnTheRight.forEach((callee) => {
			const calls: CallableEntityState[] = [
				...((entity as EntryPointEntityState).calls || [])
			];

			if (calls.includes(callee as CallableEntityState)) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
				return;
			}

			const calleeScopeOwner = getScopeOwner(callee, entity.project);

			if (
				entity.parent?.type === EntityType.Loop &&
				calleeScopeOwner?.type !== EntityType.Loop
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.ElementInLoopBodyCallingOutsideLoop
				]);
				return;
			} else if (calleeScopeOwner !== entity) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
				return;
			}

			addEntityToCompatibleList(callee);
		});

		const flattenedCalls = flattenElementCalls(
			entity as EntryPointEntityState
		);

		flattenedCalls.forEach((ent) => {
			if (ent.id === entity.id) {
				return false;
			}

			if (
				flattenedRelatedEntitiesOnTheRight.includes(
					ent as CanvasEntityState
				)
			) {
				return false;
			}

			if (
				flattenedRelatedEntitiesOnTheLeft.includes(
					ent as CanvasEntityState
				)
			) {
				return false;
			}

			const targetScopeOwner = getScopeOwner(
				ent as CanvasEntityState,
				entity.project
			);

			if (
				checkIsBranchDependentButNotDirectlyOnBranch(
					ent as CanvasEntityState,
					entity as CanvasEntityState
				)
			) {
				if (
					!compatibleEntities[ent.id] &&
					!incompatibleEntities[ent.id]
				) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.CannotCallAncestor
					]);
				}
			} else if (targetScopeOwner !== entity) {
				addEntityToIncompatibleList(ent, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
			} else {
				if (
					!compatibleEntities[ent.id] &&
					!incompatibleEntities[ent.id]
				) {
					addEntityToCompatibleList(ent);
				}
			}
		});

		const allNonCalledGlobals = [
			...entity.project.functionCalls,
			...entity.project.variableDeclarations,
			...entity.project.variableInstances,
			...entity.project.loops,
			...entity.project.conditions,
			...entity.project.operations,
			...entity.project.searches
		].filter((ent) => !ent.calledBy.length);

		for (const global of allNonCalledGlobals) {
			await tracker.tick();

			// Disable also other variable-declarations that are global
			// IF any of their variable-instances is in a scope incompatible with the current entity,
			// since connecting it would make the declaration incompatible
			if (global.type === EntityType.VariableDeclaration) {
				const ownScopeOwner = getScopeOwner(entity, entity.project);
				const currentIsGlobal =
					ownScopeOwner?.type === EntityType.Project;

				if (
					!!ownScopeOwner &&
					!global.variableInstances.every((varInst) => {
						// Check if the variable-instance is in a scope compatible with the current entity
						const instanceScopeOwner = getScopeOwner(
							varInst,
							entity.project
						);

						const compatibleWithCurrentEntityScope =
							currentIsGlobal ||
							(!!instanceScopeOwner &&
								checkIsNestedScope(
									ownScopeOwner,
									instanceScopeOwner,
									entity.project
								)) ||
							instanceScopeOwner?.id === ownScopeOwner?.id;

						return compatibleWithCurrentEntityScope;
					})
				) {
					addEntityToIncompatibleList(global, [
						CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
					]);
					continue;
				}
			}

			if (
				!compatibleEntities[global.id] &&
				!incompatibleEntities[global.id]
			) {
				addEntityToCompatibleList(global);
			}
		}
	} else if ([ConnectionNodeType.SuccessCaller].includes(connectionType)) {
		const ownAncestors = flattenCallers(entity as CallableEntityState);

		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheLeft,
			[CanvasEntityConnectionDisabledReason.CannotCallAncestor]
		);

		const localRelatedEntitiesOnTheRight: (
			| CanvasEntityState
			| InternalCallState
		)[] = flattenedRelatedEntitiesOnTheRight;

		const allVariablesOnTheRight =
			flattenedRelatedEntitiesOnTheRight.filter((ent) =>
				VARIABLE_TYPES.includes(ent.type)
			) as VariableState[];

		const allVariablesWithoutValueSetting = allVariablesOnTheRight.filter(
			(ent) =>
				!ent.readsValue &&
				!ent.writesValues.length &&
				!ent.inputs.some((input) => !!input.readsValue) &&
				!ent.outputs.some((output) => !!output.writesValues.length)
		);

		const allAvailableInternalCalls = allVariablesWithoutValueSetting
			.flatMap((ent) => ent.internalCalls)
			.filter((internalCall) => internalCall.id !== entity.id);

		localRelatedEntitiesOnTheRight.push(...allAvailableInternalCalls);

		allAvailableInternalCalls.forEach((internalCall) => {
			// If the internal call is already called by the entity, we should add it to the incompatible list
			if (
				[
					...(internalCall as InternalCallState).successCalls,
					...(internalCall as InternalCallState).errorCalls
				].includes(entity as CallableEntityState)
			) {
				addEntityToIncompatibleList(internalCall, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
			}

			// if the internal call is part of a variable in which another internal call is being executed,
			// we should add it to the incompatible list
			const parentVar = internalCall.parent;
			const otherInternalCallsOfParent =
				parentVar.usedInternalCalls.filter(
					(call) => call.id !== entity.id
				);

			if (otherInternalCallsOfParent.length) {
				addEntityToIncompatibleList(internalCall, [
					CanvasEntityConnectionDisabledReason.SiblingInternalCallAlreadyUsedInParentVariable
				]);
			}
		});

		const ownScopeOwner = getScopeOwner(entity, entity.project) as
			| EntityWithLogicScopeState
			| ProjectState;

		// If the two entities are already, we should add the entity to the incompatible list
		localRelatedEntitiesOnTheRight.forEach((callee) => {
			const calls: CallableEntityState[] = [
				...((entity as PassThroughCallableEntityState).successCalls ||
					[])
			];

			if (calls.includes(callee as CallableEntityState)) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
				return;
			}

			const calleeScopeOwner = getScopeOwner(callee, entity.project);

			if (ownScopeOwner !== calleeScopeOwner) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
				return;
			}

			// Here add any callables that trace their ancestors to the current entity from success as incompatible
			// Since the current entity cannot call on error the same entities that it calls on success
			const flattenedWithCallTypes = flattenElementCallsWithCallType(
				entity as PassThroughCallableEntityState
			);

			const callsThroughError = flattenedWithCallTypes.find(
				(flattened) =>
					flattened.callType === 'error' &&
					flattened.callee.id === callee.id
			);

			const calledAnyWay = flattenedWithCallTypes.find(
				(flattened) => flattened.callee.id === callee.id
			);

			if (!!callsThroughError) {
				addEntityToIncompatibleList(callsThroughError.callee, [
					CanvasEntityConnectionDisabledReason.CalledBySuccessCannotBeConnectedToEntitiesCalledByOwnErrorBranch
				]);
			} else if (!!calledAnyWay) {
				addEntityToIncompatibleList(calledAnyWay.callee, [
					CanvasEntityConnectionDisabledReason.RedundantCall
				]);
			}

			// We want to find if the current 'callee' is in an error branch.
			// If so, then we check if the error branch also contains the current entity, otherwise, it is out of limits
			const calleeAncestors = flattenCallersWithCallType(
				callee as CallableEntityState
			);

			const errorCaller = calleeAncestors.find(
				(callerRes) => callerRes.callType === 'error'
			);

			if (!!errorCaller) {
				const errorCallerCalls = flattenElementCallsWithCallType(
					errorCaller.caller
				);

				const errorCallerCallsCurrent = errorCallerCalls.find(
					(c) => c.callee?.id === entity.id
				);

				const errorCallerCallsCurrentAsSuccess =
					errorCallerCallsCurrent?.callType !== 'error';

				const currentCallsErrorCaller = flattenedWithCallTypes.find(
					(c) => c.callee?.id === errorCaller.caller.id
				);

				if (!!currentCallsErrorCaller) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
					]);
				} else if (errorCallerCallsCurrentAsSuccess) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
					]);
				}
			}

			if (
				!compatibleEntities[callee.id] &&
				!incompatibleEntities[callee.id]
			) {
				addEntityToCompatibleList(callee);
			}
		});

		for (const callee of ownScopeOwner?.type === EntityType.Project
			? []
			: flattenElementCalls(ownScopeOwner)) {
			await tracker.tick();
			if (callee.id === entity.id) {
				continue;
			}

			if (
				localRelatedEntitiesOnTheRight.includes(
					callee as CanvasEntityState
				)
			) {
				continue;
			}

			const calleeCalls = CALLER_TYPES.includes(callee.type)
				? flattenElementCallsWithCallType(callee as CallerEntityState)
				: [];

			// For every callee of the entry-point,
			// we want check that their branches are compatible
			// If the current callee calls the current entity on success, then all entities it calls on error should be incompatible.
			// And if the current callee calls the current entity on error, then all entities it calls on success should be incompatible.
			const callsCurrentEntityOnSuccess = calleeCalls.find(
				(flattened) =>
					flattened.callee.id === entity.id &&
					flattened.callType === 'success'
			);

			const callsCurrentEntityOnError = calleeCalls.find(
				(flattened) =>
					flattened.callee.id === entity.id &&
					flattened.callType === 'error'
			);

			if (!!callsCurrentEntityOnSuccess) {
				calleeCalls.forEach((call) => {
					if (call.callType === 'error') {
						addEntityToIncompatibleList(call.callee, [
							CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
						]);
					}
				});
			}

			if (!!callsCurrentEntityOnError) {
				calleeCalls.forEach((call) => {
					if (call.callType === 'success') {
						addEntityToIncompatibleList(call.callee, [
							CanvasEntityConnectionDisabledReason.CalledBySuccessCannotBeConnectedToEntitiesCalledByOwnErrorBranch
						]);
					}
				});
			}

			if (
				entity.type === EntityType.VariableInstance &&
				entity.declaration?.id === callee.id
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
				]);
			}

			if (
				entity.type === EntityType.InternalCall &&
				getDeclaration(entity.parent)?.id === callee.id
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
				]);
			}

			if (
				flattenedRelatedEntitiesOnTheLeft.includes(
					callee as CanvasEntityState
				)
			) {
				continue;
			}

			const targetScopeOwner = getScopeOwner(
				callee as CanvasEntityState,
				entity.project
			);

			if (
				checkIsBranchDependentButNotDirectlyOnBranch(
					callee as CanvasEntityState,
					entity as CanvasEntityState
				)
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.CannotCallAncestor
				]);
			} else if (targetScopeOwner !== ownScopeOwner) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
			} else {
				// Here add any callables that trace their ancestors to the current entity from success as incompatible
				// Since the current entity cannot call on error the same entities that it calls on success
				const flattenedWithCallTypes = flattenElementCallsWithCallType(
					entity as PassThroughCallableEntityState
				);

				const callsThroughError = flattenedWithCallTypes.find(
					(flattened) =>
						flattened.callType === 'error' &&
						flattened.callee.id === callee.id
				);

				if (!!callsThroughError) {
					addEntityToIncompatibleList(callsThroughError.callee, [
						CanvasEntityConnectionDisabledReason.CalledBySuccessCannotBeConnectedToEntitiesCalledByOwnErrorBranch
					]);
				}

				// Find all 'variable-declarations' in the 'calleeCalls'
				// The find if any 'internal-call' or 'variable-instance' of those 'variable-declarations' are calling the current entity in their call stack
				// If so, add the current entity to the incompatible list because 'cannot-be-called-before-declaration'
				const varDeclCalls = calleeCalls.filter(
					(call) =>
						call.callee.type === EntityType.VariableDeclaration
				);

				if (callee.type === EntityType.VariableDeclaration) {
					varDeclCalls.push({
						callee: callee,
						callType: 'success'
					});
				}

				for (const varDeclCall of varDeclCalls) {
					await tracker.tick();
					const varDecl =
						varDeclCall.callee as VariableDeclarationState;

					const hasInternalCallOrVarInstCallingCurrentEntity =
						ownAncestors.some((ancestor) => {
							if (ancestor.type === EntityType.VariableInstance) {
								return ancestor.declaration?.id === varDecl.id;
							} else if (
								ancestor.type === EntityType.InternalCall
							) {
								return (
									getDeclaration(ancestor.parent)?.id ===
									varDecl.id
								);
							}

							return false;
						});

					if (hasInternalCallOrVarInstCallingCurrentEntity) {
						addEntityToIncompatibleList(callee, [
							CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
						]);
						break;
					}
				}

				// We want to find if the current 'callee' is in an error branch.
				// If so, then we check if the error branch also contains the current entity, otherwise, it is out of limits
				const calleeAncestors = flattenCallersWithCallType(
					callee as CallableEntityState
				);

				const errorCaller = calleeAncestors.find(
					(callerRes) => callerRes.callType === 'error'
				);

				if (!!errorCaller) {
					const errorCallerCalls = flattenElementCallsWithCallType(
						errorCaller.caller
					);

					const errorCallerCallsCurrent = errorCallerCalls.find(
						(c) => c.callee?.id === entity.id
					);

					const errorCallerCallsCurrentAsSuccess =
						errorCallerCallsCurrent?.callType !== 'error';

					const currentCallsErrorCaller = flattenedWithCallTypes.find(
						(c) => c.callee?.id === errorCaller.caller.id
					);

					if (!!currentCallsErrorCaller) {
						addEntityToIncompatibleList(callee, [
							CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
						]);
					} else if (errorCallerCallsCurrentAsSuccess) {
						addEntityToIncompatibleList(callee, [
							CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
						]);
					}
				}

				if (
					!compatibleEntities[callee.id] &&
					!incompatibleEntities[callee.id]
				) {
					addEntityToCompatibleList(callee);
				}
			}
		}

		// If the endity is global, all other globals can be connected
		if (entity.parent === entity.project) {
			const allGlobals = [
				...entity.project.functionCalls,
				...entity.project.variableDeclarations,
				...entity.project.variableInstances,
				...entity.project.loops,
				...entity.project.conditions,
				...entity.project.operations,
				...entity.project.searches
			].filter((ent) => ent.id !== entity.id);

			for (const global of allGlobals) {
				await tracker.tick();

				if (global.type === EntityType.VariableDeclaration) {
					addEntityToIncompatibleList(global, [
						CanvasEntityConnectionDisabledReason.CannotCallGlobalVariableDeclaration
					]);
					continue;
				}

				if (
					!compatibleEntities[global.id] &&
					!incompatibleEntities[global.id]
				) {
					addEntityToCompatibleList(global);
				}
			}
		} else {
			const allNonCalledGlobals = [
				...entity.project.functionCalls,
				...entity.project.variableDeclarations,
				...entity.project.variableInstances,
				...entity.project.loops,
				...entity.project.conditions,
				...entity.project.operations,
				...entity.project.searches
			].filter((ent) => !ent.calledBy.length);

			for (const global of allNonCalledGlobals) {
				await tracker.tick();

				// Disable also other variable-declarations that are global
				// IF any of their variable-instances is in a scope incompatible with the current entity,
				// since connecting it would make the declaration incompatible
				if (global.type === EntityType.VariableDeclaration) {
					const ownScopeOwner = getScopeOwner(entity, entity.project);
					const currentIsGlobal =
						ownScopeOwner?.type === EntityType.Project;

					if (
						!!ownScopeOwner &&
						!global.variableInstances.every((varInst) => {
							// Check if the variable-instance is in a scope compatible with the current entity
							const instanceScopeOwner = getScopeOwner(
								varInst,
								entity.project
							);

							const compatibleWithCurrentEntityScope =
								currentIsGlobal ||
								(!!instanceScopeOwner &&
									checkIsNestedScope(
										ownScopeOwner,
										instanceScopeOwner,
										entity.project
									)) ||
								instanceScopeOwner?.id === ownScopeOwner?.id;

							return compatibleWithCurrentEntityScope;
						})
					) {
						addEntityToIncompatibleList(global, [
							CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
						]);
					}

					// We have to make sure that the current or any previous caller entity is a 'variable-instance' or 'internal-call' of the 'variable-declaration',
					// otherwise the 'variable-declaration' would be called before declaration
					if (entity.type === EntityType.VariableInstance) {
						const isVarInstOfVarDecl =
							entity.declaration?.id === global.id;

						if (isVarInstOfVarDecl) {
							addEntityToIncompatibleList(global, [
								CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
							]);
						}
					} else if (entity.type === EntityType.InternalCall) {
						const declarationOfInternalCall = getDeclaration(
							entity.parent
						);

						const isInternalCallOfVarDecl =
							declarationOfInternalCall?.id === global.id;

						if (isInternalCallOfVarDecl) {
							addEntityToIncompatibleList(global, [
								CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
							]);
						}
					}

					ownAncestors.forEach((ancestor) => {
						if (ancestor.type === EntityType.VariableInstance) {
							const isVarInstOfVarDecl =
								ancestor.declaration?.id === global.id;

							if (isVarInstOfVarDecl) {
								addEntityToIncompatibleList(global, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
							}
						} else if (ancestor.type === EntityType.InternalCall) {
							const declarationOfInternalCall = getDeclaration(
								ancestor.parent
							);

							const isInternalCallOfVarDecl =
								declarationOfInternalCall?.id === global.id;

							if (isInternalCallOfVarDecl) {
								addEntityToIncompatibleList(global, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
							}
						}
					});
				}

				if (
					!compatibleEntities[global.id] &&
					!incompatibleEntities[global.id]
				) {
					addEntityToCompatibleList(global);
				}
			}
		}
	} else if ([ConnectionNodeType.ErrorCaller].includes(connectionType)) {
		const ownAncestors = flattenCallersWithCallType(
			entity as CallableEntityState
		);

		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheLeft,
			[CanvasEntityConnectionDisabledReason.CannotCallAncestor]
		);

		const ownScopeOwner = getScopeOwner(entity, entity.project) as
			| EntityWithLogicScopeState
			| ProjectState;

		// If the two entities are already, we should add the entity to the incompatible list
		flattenedRelatedEntitiesOnTheRight.forEach((callee) => {
			const calls: CallableEntityState[] = [
				...((entity as PassThroughCallableEntityState).errorCalls || [])
			];

			if (calls.includes(callee as CallableEntityState)) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.ExecutionsAlreadyConnected
				]);
				return;
			}

			const calleeScopeOwner = getScopeOwner(callee, entity.project);

			if (ownScopeOwner !== calleeScopeOwner) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
				return;
			}

			// Here add any callables that trace their ancestors to the current entity from success as incompatible
			// Since the current entity cannot call on error the same entities that it calls on success
			const flattenedWithCallTypes = flattenElementCallsWithCallType(
				entity as PassThroughCallableEntityState
			);

			const callsThroughSuccess = flattenedWithCallTypes.find(
				(flattened) =>
					flattened.callType === 'success' &&
					flattened.callee.id === callee.id
			);

			const calledAnyWay = flattenedWithCallTypes.find(
				(flattened) => flattened.callee.id === callee.id
			);

			if (!!callsThroughSuccess) {
				addEntityToIncompatibleList(callsThroughSuccess.callee, [
					CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
				]);
			} else if (!!calledAnyWay) {
				addEntityToIncompatibleList(calledAnyWay.callee, [
					CanvasEntityConnectionDisabledReason.RedundantCall
				]);
			}

			if (
				!compatibleEntities[callee.id] &&
				!incompatibleEntities[callee.id]
			) {
				addEntityToCompatibleList(callee);
			}
		});

		for (const callee of ownScopeOwner?.type === EntityType.Project
			? []
			: flattenElementCalls(ownScopeOwner)) {
			await tracker.tick();
			if (callee.id === entity.id) {
				continue;
			}

			if (
				flattenedRelatedEntitiesOnTheRight.includes(
					callee as CanvasEntityState
				)
			) {
				continue;
			}

			// For every callee of the entry-point,
			// we want check that their branches are compatible
			// If the current callee calls the current entity on success, then all entities it calls on error should be incompatible.
			// And if the current callee calls the current entity on error, then all entities it calls on success should be incompatible.
			const calleeCalls = CALLER_TYPES.includes(callee.type)
				? flattenElementCallsWithCallType(callee as CallerEntityState)
				: [];

			const callsCurrentEntityOnSuccess = calleeCalls.find(
				(flattened) =>
					flattened.callee.id === entity.id &&
					flattened.callType === 'success'
			);

			const callsCurrentEntityOnError = calleeCalls.find(
				(flattened) =>
					flattened.callee.id === entity.id &&
					flattened.callType === 'error'
			);

			if (!!callsCurrentEntityOnSuccess) {
				calleeCalls.forEach((call) => {
					if (call.callType === 'error') {
						addEntityToIncompatibleList(call.callee, [
							CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
						]);
					}
				});
			}

			if (!!callsCurrentEntityOnError) {
				calleeCalls.forEach((call) => {
					if (call.callType === 'success') {
						addEntityToIncompatibleList(call.callee, [
							CanvasEntityConnectionDisabledReason.CalledBySuccessCannotBeConnectedToEntitiesCalledByOwnErrorBranch
						]);
					}
				});
			}

			// If any ancestor of the current entity
			// is calling the callee or any of its descendants on a success branch,
			// then the callee cannot be connected to the current entity on error
			// because it would create an execution between the callee and its ancestor that would be called both on success and error
			ownAncestors.forEach((item) => {
				const ancestorsCalls = flattenElementCallsWithCallType(
					item.caller as CallerEntityState
				);

				const callsCalleeOnSuccess = ancestorsCalls.find(
					(flattened) =>
						flattened.callee.id === callee.id &&
						flattened.callType === 'success'
				);

				if (!!callsCalleeOnSuccess) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
					]);
				}

				calleeCalls.forEach((call) => {
					if (call.callType === 'success') {
						const callsAncestorOnSuccess = ancestorsCalls.find(
							(flattened) =>
								flattened.callee.id === item.caller.id &&
								flattened.callType === 'success'
						);

						if (!!callsAncestorOnSuccess) {
							addEntityToIncompatibleList(call.callee, [
								CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
							]);
						}
					}
				});
			});

			if (
				entity.type === EntityType.VariableInstance &&
				entity.declaration?.id === callee.id
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
				]);
			}

			if (
				entity.type === EntityType.InternalCall &&
				getDeclaration(entity.parent)?.id === callee.id
			) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
				]);
			}

			if (
				flattenedRelatedEntitiesOnTheLeft.includes(
					callee as CanvasEntityState
				)
			) {
				continue;
			}

			const targetScopeOwner = getScopeOwner(
				callee as CanvasEntityState,
				entity.project
			);

			if (
				checkIsBranchDependentButNotDirectlyOnBranch(
					callee as CanvasEntityState,
					entity as CanvasEntityState
				)
			) {
				if (
					!compatibleEntities[callee.id] &&
					!incompatibleEntities[callee.id]
				) {
					addEntityToIncompatibleList(callee, [
						CanvasEntityConnectionDisabledReason.CannotCallAncestor
					]);
				}
			} else if (targetScopeOwner !== ownScopeOwner) {
				addEntityToIncompatibleList(callee, [
					CanvasEntityConnectionDisabledReason.OtherIncompatibleScopeReason
				]);
			} else {
				// Here add any callables that trace their ancestors to the current entity from success as incompatible
				// Since the current entity cannot call on error the same entities that it calls on success
				const flattenedWithCallTypes = flattenElementCallsWithCallType(
					entity as PassThroughCallableEntityState
				);

				const callsThroughSuccess = flattenedWithCallTypes.find(
					(flattened) =>
						flattened.callType === 'success' &&
						flattened.callee.id === callee.id
				);

				if (!!callsThroughSuccess) {
					addEntityToIncompatibleList(callsThroughSuccess.callee, [
						CanvasEntityConnectionDisabledReason.CalledByErrorCannotBeConnectedToEntitiesCalledByOwnSuccessBranch
					]);
				}

				// Find all 'variable-declarations' in the 'calleeCalls'
				// The find if any 'internal-call' or 'variable-instance' of those 'variable-declarations' are calling the current entity in their call stack
				// If so, add the current entity to the incompatible list because 'cannot-be-called-before-declaration'
				const varDeclCalls = calleeCalls.filter(
					(call) =>
						call.callee.type === EntityType.VariableDeclaration
				);

				if (callee.type === EntityType.VariableDeclaration) {
					varDeclCalls.push({
						callee: callee,
						callType: 'success'
					});
				}

				for (const varDeclCall of varDeclCalls) {
					await tracker.tick();
					const varDecl =
						varDeclCall.callee as VariableDeclarationState;

					const hasInternalCallOrVarInstCallingCurrentEntity =
						ownAncestors.some((item) => {
							if (
								item.caller.type === EntityType.VariableInstance
							) {
								return (
									item.caller.declaration?.id === varDecl.id
								);
							} else if (
								item.caller.type === EntityType.InternalCall
							) {
								return (
									getDeclaration(item.caller.parent)?.id ===
									varDecl.id
								);
							}

							return false;
						});

					if (hasInternalCallOrVarInstCallingCurrentEntity) {
						addEntityToIncompatibleList(callee, [
							CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
						]);
						break;
					}
				}

				if (
					!compatibleEntities[callee.id] &&
					!incompatibleEntities[callee.id]
				) {
					addEntityToCompatibleList(callee);
				}
			}
		}

		// If the endity is global, all other globals can be connected
		if (entity.parent === entity.project) {
			const allGlobals = [
				...entity.project.functionCalls,
				...entity.project.variableDeclarations,
				...entity.project.variableInstances,
				...entity.project.loops,
				...entity.project.conditions,
				...entity.project.operations,
				...entity.project.searches
			].filter((ent) => ent.id !== entity.id);

			for (const global of allGlobals) {
				await tracker.tick();

				if (global.type === EntityType.VariableDeclaration) {
					addEntityToIncompatibleList(global, [
						CanvasEntityConnectionDisabledReason.CannotCallGlobalVariableDeclaration
					]);
					continue;
				}

				if (
					!compatibleEntities[global.id] &&
					!incompatibleEntities[global.id]
				) {
					addEntityToCompatibleList(global);
				}
			}
		} else {
			const allNonCalledGlobals = [
				...entity.project.functionCalls,
				...entity.project.variableDeclarations,
				...entity.project.variableInstances,
				...entity.project.loops,
				...entity.project.conditions,
				...entity.project.operations,
				...entity.project.searches
			].filter((ent) => !ent.calledBy.length);

			for (const global of allNonCalledGlobals) {
				await tracker.tick();

				// Disable also other variable-declarations that are global
				// IF any of their variable-instances is in a scope incompatible with the current entity,
				// since connecting it would make the declaration incompatible
				if (global.type === EntityType.VariableDeclaration) {
					const ownScopeOwner = getScopeOwner(entity, entity.project);
					const currentIsGlobal =
						ownScopeOwner?.type === EntityType.Project;

					if (
						!!ownScopeOwner &&
						!global.variableInstances.every((varInst) => {
							// Check if the variable-instance is in a scope compatible with the current entity
							const instanceScopeOwner = getScopeOwner(
								varInst,
								entity.project
							);

							const compatibleWithCurrentEntityScope =
								currentIsGlobal ||
								(!!instanceScopeOwner &&
									checkIsNestedScope(
										ownScopeOwner,
										instanceScopeOwner,
										entity.project
									)) ||
								instanceScopeOwner?.id === ownScopeOwner?.id;

							return compatibleWithCurrentEntityScope;
						})
					) {
						addEntityToIncompatibleList(global, [
							CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
						]);
					}

					// We have to make sure that the current or any previous caller entity is a 'variable-instance' or 'internal-call' of the 'variable-declaration',
					// otherwise the 'variable-declaration' would be called before declaration
					if (entity.type === EntityType.VariableInstance) {
						const isVarInstOfVarDecl =
							entity.declaration?.id === global.id;

						if (isVarInstOfVarDecl) {
							addEntityToIncompatibleList(global, [
								CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
							]);
						}
					} else if (entity.type === EntityType.InternalCall) {
						const declarationOfInternalCall = getDeclaration(
							entity.parent
						);

						const isInternalCallOfVarDecl =
							declarationOfInternalCall?.id === global.id;

						if (isInternalCallOfVarDecl) {
							addEntityToIncompatibleList(global, [
								CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
							]);
						}
					}

					ownAncestors.forEach((item) => {
						if (item.caller.type === EntityType.VariableInstance) {
							const isVarInstOfVarDecl =
								item.caller.declaration?.id === global.id;

							if (isVarInstOfVarDecl) {
								addEntityToIncompatibleList(global, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
							}
						} else if (
							item.caller.type === EntityType.InternalCall
						) {
							const declarationOfInternalCall = getDeclaration(
								item.caller.parent
							);

							const isInternalCallOfVarDecl =
								declarationOfInternalCall?.id === global.id;

							if (isInternalCallOfVarDecl) {
								addEntityToIncompatibleList(global, [
									CanvasEntityConnectionDisabledReason.CannotBeCalledBeforeDeclaration
								]);
							}
						}
					});
				}

				if (
					!compatibleEntities[global.id] &&
					!incompatibleEntities[global.id]
				) {
					addEntityToCompatibleList(global);
				}
			}
		}
	} else if ([ConnectionNodeType.PropertyOut].includes(connectionType)) {
		console.log(ConnectionNodeType.PropertyOut);

		const parentCallerEntity = getParentCallerEntity(entity);

		await addEntitiesToIncompatibleListAsync(
			flattenedRelatedEntitiesOnTheLeft,
			[CanvasEntityConnectionDisabledReason.CannotWriteValueToAncestor]
		);

		let relevantEntitiesOnTheRight: CanvasEntityState[] =
			flattenedRelatedEntitiesOnTheRight;

		// Error outputs can only connect to the error-calls branch calees, of their parent entity
		if (
			entity.type === EntityType.OutputMap &&
			entity.parentRelationType === OutputMapParentChildRelation.Error
		) {
			const flatErrorCalls = await flattenCallsAsync(
				entity.parent.errorCalls,
				{
					tracker
				}
			);

			relevantEntitiesOnTheRight = [];

			flattenedRelatedEntitiesOnTheRight.forEach((ent) => {
				if (
					flatErrorCalls.includes(
						ent as DraggableCallableEntityState as CallableEntityState
					)
				) {
					relevantEntitiesOnTheRight.push(ent as CanvasEntityState);
				} else {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.ErrorOututMapCannotBeConnectedToParentSuccessBranchCallees
					]);

					const allValueReaders = getAllValueReadingChildren(
						ent as CanvasEntityState
					);

					allValueReaders.forEach((vr) => {
						addEntityToIncompatibleList(vr, [
							CanvasEntityConnectionDisabledReason.ErrorOututMapCannotBeConnectedToParentSuccessBranchCallees
						]);
					});
				}
			});
		} else if (
			PASS_THROUGH_CALLABLE_TYPES_WITH_OUTPUTS.includes(
				parentCallerEntity.type
			)
		) {
			const flatSuccessCalls = await flattenCallsAsync(
				(
					parentCallerEntity as PassThroughCallableEntityWithOutputsState
				).successCalls,
				{ tracker }
			);

			relevantEntitiesOnTheRight = [];

			flattenedRelatedEntitiesOnTheRight.forEach((ent) => {
				if (
					flatSuccessCalls.includes(
						ent as DraggableCallableEntityState as CallableEntityState
					)
				) {
					relevantEntitiesOnTheRight.push(ent as CanvasEntityState);
				} else {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.SuccessOutputMapCannotBeConnectedToParentErrorBranchCallees
					]);

					const allValueReaders = getAllValueReadingChildren(
						ent as CanvasEntityState
					);

					allValueReaders.forEach((vr) => {
						addEntityToIncompatibleList(vr, [
							CanvasEntityConnectionDisabledReason.SuccessOutputMapCannotBeConnectedToParentErrorBranchCallees
						]);
					});
				}
			});
		}

		const allValueReadingEntities = relevantEntitiesOnTheRight.flatMap(
			(ent) => {
				const valReadingEnts = getAllValueReadingChildren(
					ent as CanvasEntityState
				);

				if (VALUE_READING_TYPES.includes(ent.type)) {
					valReadingEnts.push(ent as ValueReadingEntityState);
				}

				return valReadingEnts;
			}
		);

		// There might be entities in a global scope that if connected, would be compatible.
		// If the 'allowReadWriteToFixableScope' option is enabled, we want to allow connections to those entities,
		// We should gather here all value writing entities on a global scope.
		// If they aren't yet called-by anything, we directly add them to 'compatible' with 'requiresCallConnectionFrom' as itself, and the 'requiresCallConnectionTo' field pointing to the current canvas parent entity.
		// if they are already called by others, we add them to incompatible but add the first entity in their call-stack as the 'requiresCallConnectionFrom' and the current canvas parent entity as the 'requiresCallConnectionTo'
		if (options.allowReadWriteToFixableScope) {
			const potentialNewScopeOwner = getScopeOwner(
				entity,
				entity.project
			);
			const ownAncestors = flattenCallers(entity as CallableEntityState);

			const allGlobalValueReadingEntities = [
				...(await getAllGlobalValueReadingEntities(entity.project, {
					tracker
				})),
				...(!!potentialNewScopeOwner &&
				potentialNewScopeOwner.type !== EntityType.Project
					? flattenElementCalls(
							potentialNewScopeOwner as EntityWithLogicScopeState
						)
							.filter(
								(call) =>
									call.id !== entity.id &&
									!ownAncestors.find((c) => c.id === call.id)
							)
							.flatMap((call) => getAllValueReadingChildren(call))
					: [])
			];

			allGlobalValueReadingEntities.forEach((reader) => {
				if (allValueReadingEntities.includes(reader)) {
					return;
				}

				const readerParentCallableEntity =
					getParentCallableEntity(reader);
				const cantChangeScopeReasons: CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage[] =
					[];

				if (
					!!potentialNewScopeOwner &&
					readerParentCallableEntity?.type ===
						EntityType.VariableDeclaration &&
					!readerParentCallableEntity.variableInstances.every(
						(varInst) => {
							const instanceScopeOwner = getScopeOwner(
								varInst,
								entity.project
							);

							const compatibleWithNewScope =
								!!instanceScopeOwner &&
								(checkIsNestedScope(
									potentialNewScopeOwner,
									instanceScopeOwner,
									entity.project
								) ||
									potentialNewScopeOwner?.id ===
										instanceScopeOwner?.id);

							return compatibleWithNewScope;
						}
					)
				) {
					cantChangeScopeReasons.push(
						CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
					);
				}

				if (!!parentCallerEntity && !!readerParentCallableEntity) {
					const readerParentAggCalls = [
						...((
							readerParentCallableEntity as PassThroughCallableEntityState
						).successCalls || []),
						...((
							readerParentCallableEntity as PassThroughCallableEntityState
						).errorCalls || [])
					];

					const alreadyCalledByOtherEntitiesInDifferentScope =
						readerParentCallableEntity.calledBy.some(
							(caller) =>
								getScopeOwner(caller, entity.project)?.id !==
								potentialNewScopeOwner?.id
						);

					if (
						!alreadyCalledByOtherEntitiesInDifferentScope &&
						!readerParentAggCalls.length &&
						!cantChangeScopeReasons.length
					) {
						compatibleIfScopeChange[reader.id] = {
							entity: reader,
							requiresCallConnectionTo:
								readerParentCallableEntity,
							requiresCallConnectionToFrom:
								parentCallerEntity as CallerEntityState
						};
						allValueReadingEntities.push(reader);
					} else {
						incompatibleButFixableWithScopeChange[reader.id] = {
							entity: reader,
							requiresCallConnectionTo:
								readerParentCallableEntity,
							requiresCallConnectionToFrom:
								parentCallerEntity as CallerEntityState,
							reasons: [
								...(readerParentAggCalls.length
									? [
											CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
										]
									: []),
								...(alreadyCalledByOtherEntitiesInDifferentScope
									? [
											CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
										]
									: []),
								...cantChangeScopeReasons
							] as (
								| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItIsAlreadyCalledByOtherEntitiesInDifferentScope
								| CanvasEntityConnectionDisabledReason.CannotChangeScopeBecauseItCallsOtherEntities
								| CanvasEntityConnectionDisabledReason.VariableDeclarationCannotBeMovedToLowerScopeBecauseInstancesUsage
							)[]
						};
					}
				}
			});
		}

		// If the two entities are already, we should add the entity to the incompatible list
		allValueReadingEntities.forEach((reader) => {
			if (!!reader.readsValue) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.CannotReadMoreThanOneValue
				]);
			}

			if (
				reader.type === EntityType.InputMap &&
				(reader.parent.type === EntityType.VariableDeclaration ||
					reader.parent.type === EntityType.VariableInstance) &&
				reader.parent.internalCalls.some(
					(call) => !!call.calledBy.length
				)
			) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.CannotInteractWithInputInVariableWithInternalCalls
				]);
			}

			if (
				reader.type === EntityType.InputMap &&
				reader.parent.type === EntityType.VariableDeclaration &&
				reader.parent.parent.type === EntityType.Project
			) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.CannotInteractWithInputInGlobalVariableDeclaration
				]);
			}

			if (
				(entity as ValueWritingEntityState).writesValues.includes(
					reader
				)
			) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.ValuesAlreadyConnected
				]);
				return;
			}

			const representsConstantProperty =
				reader.type === EntityType.InputMap &&
				reader.parent.type === EntityType.VariableInstance &&
				reader.declaration?.type === EntityType.Property &&
				reader.declaration?.constant;

			if (representsConstantProperty) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.PropertyIsConstantItsValueCantBeReset
				]);
			}

			const constantVar =
				reader.type === EntityType.VariableInstance &&
				reader.declaration?.constant;

			if (constantVar) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.CannotReSetValueOfConstantVariable
				]);
			}

			if (
				reader.type === EntityType.VariableDeclaration &&
				reader.parent.type === EntityType.Project &&
				!compatibleIfScopeChange[reader.id]
			) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.CannotWriteValueOfGlobalVariableDeclaration
				]);
			}

			const readerDataType = (
				reader as ValueReadingEntityState
			).getDataType(null);
			const writerDataType = (
				entity as ValueWritingEntityState
			).getDataType(null);
			const dataTypeCompatibility = checkAreDataTypesCompatible(
				writerDataType,
				readerDataType
			);

			if (!dataTypeCompatibility.compatible) {
				addEntityToIncompatibleList(reader, [
					CanvasEntityConnectionDisabledReason.DataTypesNotCompatible
				]);
				return;
			}

			if (!incompatibleEntities[reader.id]) {
				addEntityToCompatibleList(reader);
			}
		});

		relevantEntitiesOnTheRight.forEach((ent) => {
			if (!compatibleEntities[ent.id] && !incompatibleEntities[ent.id]) {
				addEntityToIncompatibleList(ent, [
					CanvasEntityConnectionDisabledReason.IsNotValueReadingEntity
				]);
			}
		});

		// When the related entities on the right are operations, conditions
		// Add the dynamic value descriptor from their declarations to the list of compatible entities
		// If they are a compatible data type
		// Value descriptors are not value reading, but this will be used to indicate that a new input can be generated
		const relatedOperationsAndConditionsOnTheRight: (
			| ConditionState
			| OperationState
		)[] = relevantEntitiesOnTheRight.filter((ent) =>
			[EntityType.Operation, EntityType.Condition].includes(ent.type)
		) as (ConditionState | OperationState)[];

		const dynamicValueDescritors =
			relatedOperationsAndConditionsOnTheRight.flatMap((ent) =>
				ent.declaration.inputs.filter(
					(input) =>
						input.parentRelationType ===
						ValueDescriptorParentChildRelation.DynamicArgument
				)
			);

		dynamicValueDescritors.forEach((valueDescriptor) => {
			if (
				checkAreDataTypesCompatible(
					(valueDescriptor as ValueDescriptorState).getDataType(null),
					(entity as ValueWritingEntityState).getDataType(null)
				).compatible
			) {
				addEntityToCompatibleList(valueDescriptor);
			}
		});
	}

	const CONNECTABLE_TYPES = [
		EntityType.FunctionDeclaration,
		EntityType.VariableDeclaration,
		EntityType.VariableInstance,
		EntityType.FunctionCall,
		EntityType.Search,
		EntityType.Operation,
		EntityType.Condition,
		EntityType.Loop,
		EntityType.InternalCall,
		EntityType.GlobalEvent,
		EntityType.ArgumentDeclaration,
		EntityType.InputMap,
		EntityType.OutputMap
	];

	for (const ent of Object.values(entity.project.instances)) {
		if (!compatibleEntities[ent.id]) {
			if (!CONNECTABLE_TYPES.includes(ent.type)) {
				addEntityToIncompatibleList(ent, [
					CanvasEntityConnectionDisabledReason.NotAConnectableEntityType
				]);
				continue;
			}

			if (connectionType === ConnectionNodeType.PropertyOut) {
				if (!VALUE_READING_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotValueReadingEntity
					]);
				}
			} else if (connectionType === ConnectionNodeType.PropertyIn) {
				if (!VALUE_WRITING_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotValueWritingEntity
					]);
				}
			} else if (connectionType === ConnectionNodeType.CalledBy) {
				if (!CALLER_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotCallerEntity
					]);
				}
			} else if (connectionType === ConnectionNodeType.CalledByError) {
				if (!PASS_THROUGH_CALLABLE_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotPassThroughCallableEntity
					]);
				}
			} else if (
				[
					ConnectionNodeType.SuccessCaller,
					ConnectionNodeType.ErrorCaller,
					ConnectionNodeType.EntryPointCaller
				].includes(connectionType)
			) {
				if (!CALLABLE_TYPES.includes(ent.type)) {
					addEntityToIncompatibleList(ent, [
						CanvasEntityConnectionDisabledReason.IsNotCallableEntity
					]);
				}
			}

			if (!incompatibleEntities[ent.id] && !compatibleEntities[ent.id]) {
				addEntityToIncompatibleList(ent, [
					CanvasEntityConnectionDisabledReason.NotInScope
				]);
			}
		}

		await tracker.tick();
	}

	return {
		compatible: compatibleEntities,
		incompatible: incompatibleEntities
	};
}

/**
 * Based what ultimately was connected ('sourceEntity', 'targetEntity' and 'connectionType'),
 * and the initial compatibility result, we might need to apply some auto-fixes to make sure the connection is valid.
 *
 * @param {UserManagedEntityState} sourceEntity The entity from which the connection starts
 * @param {UserManagedEntityState} targetEntity The entity to which the connection goes
 * @param {ConnectionNodeType} connectionType The type of the connection that was made
 * @param {ChangeSet} changeSet The changeset to which any potential change should be added
 * @param {IConnectionCompatibilityResult} compatibilityResult The initial compatibility result before the connection was made, this will be used to determine if any auto-fix is needed, and which one
 */
export async function applyConnectionCompatibilityAutoFixes(
	sourceEntity: UserManagedEntityState,
	targetEntity: UserManagedEntityState,
	connectionType: ConnectionNodeType,
	changeSet: ChangeSet,
	compatibilityResult: IConnectionCompatibilityResult,
	options?: {
		onConnection?: (
			sourceEntity: UserManagedEntityState,
			targetEntity: UserManagedEntityState,
			connectionType: ConnectionNodeType
		) => Promise<void>;
		onCallRemoved?: (
			caller: CallerEntityState,
			callee: CallableEntityState,
			callType: 'success' | 'error' | 'entry' | 'loop-body'
		) => Promise<void>;
	}
) {
	if (!sourceEntity || !targetEntity) {
		return;
	}

	const optionsWithDefaults = {
		onConnection: async () => {},
		onCallRemoved: async () => {},
		...options
	};

	let nonInitialEnt: UserManagedEntityState = sourceEntity;

	if (connectionType === ConnectionNodeType.PropertyOut) {
		nonInitialEnt = targetEntity;
	}

	const nonInitialCompatibility =
		compatibilityResult.compatible[nonInitialEnt.id];

	if (
		nonInitialCompatibility &&
		nonInitialCompatibility.requiresCallConnectionToFrom &&
		nonInitialCompatibility.requiresCallConnectionTo
	) {
		const requiredCaller =
			nonInitialCompatibility.requiresCallConnectionToFrom;
		const requiredCallee = nonInitialCompatibility.requiresCallConnectionTo;

		if (ENTRY_POINT_TYPES.includes(requiredCaller.type)) {
			(requiredCaller as EntryPointEntityState).addCall(
				requiredCallee,
				changeSet
			);
			(requiredCallee as CallableEntityState).addEntryCaller(
				requiredCaller as EntryPointEntityState,
				changeSet,
				true
			);
		} else if (PASS_THROUGH_CALLABLE_TYPES.includes(requiredCaller.type)) {
			(requiredCaller as PassThroughCallableEntityState).addSuccessCall(
				requiredCallee,
				changeSet,
				true
			);
			(requiredCallee as CallableEntityState).addSuccessCaller(
				requiredCaller as PassThroughCallableEntityState,
				changeSet,
				true
			);
		}

		await optionsWithDefaults.onConnection(
			requiredCaller,
			requiredCallee,
			ConnectionNodeType.CalledBy
		);

		if (PASS_THROUGH_CALLABLE_TYPES.includes(requiredCallee.type)) {
			moveParallelCalls(
				requiredCallee as PassThroughCallableEntityState,
				changeSet,
				{
					onConnection: optionsWithDefaults.onConnection,
					onCallRemoved: optionsWithDefaults.onCallRemoved
				}
			);

			const aggregateCalls = [
				...(requiredCallee as PassThroughCallableEntityState)
					.successCalls,
				...(requiredCallee as PassThroughCallableEntityState).errorCalls
			];

			await removeRedundantCallers(requiredCallee, changeSet, {
				onCallRemoved: optionsWithDefaults.onCallRemoved
			});

			for await (const call of aggregateCalls) {
				await removeRedundantCallers(call, changeSet, {
					onCallRemoved: optionsWithDefaults.onCallRemoved
				});
			}
		}

		const ownParentCanvasEntity = getParentCanvasEntity(requiredCallee);
		const callerParentCanvasEntity = getParentCanvasEntity(requiredCaller);

		let y = callerParentCanvasEntity.y;
		let x =
			callerParentCanvasEntity.x +
			CANVAS_HORIZONTAL_BUFFER_BETWEEN_CARDS +
			CANVAS_CARD_WIDTH;

		if (
			requiredCaller.type === EntityType.FunctionDeclaration &&
			callerParentCanvasEntity.type === EntityType.DefinitionEntity
		) {
			const { offset } = calculateVerticalOffsetForMethod(requiredCaller);

			y = callerParentCanvasEntity.y + offset;
		}

		if (!ownParentCanvasEntity.x || !ownParentCanvasEntity.y) {
			y = 0;
			x = 0;
		}

		// @ts-ignore
		ownParentCanvasEntity.metaSync(
			{
				x: x,
				y: y
			},
			changeSet
		);

		let lastEntityWithCoordinates!: CanvasEntityState;

		for (const caller of requiredCallee.calledBy) {
			const callerParentCanvasEntity = getParentCanvasEntity(caller);

			if (callerParentCanvasEntity.x && callerParentCanvasEntity.y) {
				lastEntityWithCoordinates = callerParentCanvasEntity;
				break;
			}
		}

		const childrenToRender: CanvasEntityState[] = [
			lastEntityWithCoordinates
		];

		// Re-order the calls of the required callee so that if any of them is rendered higher (smaller y number) they are before in the list
		if (PASS_THROUGH_CALLABLE_TYPES.includes(requiredCallee.type)) {
			(
				requiredCallee as PassThroughCallableEntityState
			).successCalls.sort((a, b) => {
				const aParentCanvasEntity = getParentCanvasEntity(a);
				const bParentCanvasEntity = getParentCanvasEntity(b);

				if (aParentCanvasEntity.y < bParentCanvasEntity.y) {
					return -1;
				} else if (aParentCanvasEntity.y > bParentCanvasEntity.y) {
					return 1;
				}
				return 0;
			});

			(requiredCallee as PassThroughCallableEntityState).errorCalls.sort(
				(a, b) => {
					const aParentCanvasEntity = getParentCanvasEntity(a);
					const bParentCanvasEntity = getParentCanvasEntity(b);

					if (aParentCanvasEntity.y < bParentCanvasEntity.y) {
						return -1;
					} else if (aParentCanvasEntity.y > bParentCanvasEntity.y) {
						return 1;
					}
					return 0;
				}
			);
		}

		childrenToRender.push(
			...(PASS_THROUGH_CALLABLE_TYPES.includes(requiredCallee.type)
				? [
						ownParentCanvasEntity,
						...flattenElementCalls(
							requiredCallee as PassThroughCallableEntityState,
							{
								sorting: 'local-y-axis'
							}
						)
					].map((ent) => getParentCanvasEntity(ent))
				: [ownParentCanvasEntity])
		);

		Area.autoGenerateLayout(
			lastEntityWithCoordinates || sourceEntity.project,
			changeSet,
			filterOutDuplicateEntities(childrenToRender) as CanvasEntityState[]
		);
	}
}

export function fromSourceAndTargetEntityToConnectionNodeType(
	sourceEntity: EntityState,
	targetEntity: EntityState,
	startId: string,
	endId: string
): ConnectionNodeType.CalledBy | ConnectionNodeType.PropertyIn {
	if (endId === getCanvasEntityExecutionNodeId(targetEntity)) {
		return ConnectionNodeType.CalledBy;
	} else if (
		startId === getCanvasEntitySuccessCallerNodeDOMId(sourceEntity)
	) {
		return ConnectionNodeType.CalledBy;
	} else if (startId === getCanvasEntityErrorCallerNodeDOMId(sourceEntity)) {
		return ConnectionNodeType.CalledBy;
	} else if (startId === getCanvasEntityEntryCallerNodeDOMId(sourceEntity)) {
		return ConnectionNodeType.CalledBy;
	}

	return ConnectionNodeType.PropertyIn;
}

export function connectEntitiesBasedOnConenctionType(
	sourceEntity: EntityState,
	targetEntity: EntityState,
	connectionType: ConnectionNodeType,
	changeSet: ChangeSet
) {
	switch (connectionType) {
		case ConnectionNodeType.PropertyOut:
		case ConnectionNodeType.PropertyIn: {
			if (
				VALUE_WRITING_TYPES.includes(sourceEntity.type) &&
				VALUE_READING_TYPES.includes(targetEntity.type)
			) {
				(sourceEntity as ValueWritingEntityState).addValueReader(
					targetEntity as ValueReadingEntityState,
					changeSet
				);

				(targetEntity as ValueReadingEntityState).setValueWriter(
					sourceEntity as ValueWritingEntityState,
					changeSet
				);
				changeSet.actionName === StateMutationAction.ConnectValues;

				if (targetEntity.type === EntityType.InputMap) {
					if (targetEntity.parent?.type === EntityType.Search) {
						changeSet.actionName ===
							StateMutationAction.ConnectValues;
					} else if (
						targetEntity.parent?.type === EntityType.Condition
					) {
					} else if (
						targetEntity.parent?.type === EntityType.Operation
					) {
					} else if (
						targetEntity.parent?.type === EntityType.InternalCall
					) {
					} else if (
						targetEntity.parent?.type === EntityType.ReturnStatement
					) {
					} else if (
						targetEntity.parent?.type === EntityType.BreakStatement
					) {
					} else if (
						targetEntity.parent?.type ===
						EntityType.ContinueStatement
					) {
					} else if (targetEntity.parent?.type === EntityType.Loop) {
					} else if (
						targetEntity.parent?.type ===
						EntityType.VariableDeclaration
					) {
					} else if (
						targetEntity.parent?.type ===
						EntityType.VariableInstance
					) {
					}
				} else if (
					targetEntity?.type === EntityType.VariableDeclaration
				) {
				} else if (targetEntity?.type === EntityType.VariableInstance) {
				} else if (targetEntity?.type === EntityType.ReturnStatement) {
				} else if (targetEntity?.type === EntityType.BreakStatement) {
				} else if (
					targetEntity?.type === EntityType.ContinueStatement
				) {
				}
			}
			return;
		}
		case ConnectionNodeType.CalledBy: {
			if (
				CALLER_TYPES.includes(sourceEntity.type) &&
				CALLABLE_TYPES.includes(targetEntity.type)
			) {
				if (ENTRY_POINT_TYPES.includes(sourceEntity.type)) {
					(sourceEntity as EntryPointEntityState).addCall(
						targetEntity as CallableEntityState,
						changeSet
					);

					(targetEntity as CallableEntityState).addEntryCaller(
						sourceEntity as EntryPointEntityState,
						changeSet,
						true
					);
					changeSet.actionName ===
						StateMutationAction.ConnectExecution;
				} else if (
					PASS_THROUGH_CALLABLE_TYPES.includes(sourceEntity.type)
				) {
					(
						sourceEntity as PassThroughCallableEntityState
					).addSuccessCall(
						targetEntity as CallableEntityState,
						changeSet,
						true
					);

					(targetEntity as CallableEntityState).addSuccessCaller(
						sourceEntity as PassThroughCallableEntityState,
						changeSet,
						true
					);
					changeSet.actionName ===
						StateMutationAction.ConnectExecution;
				}
			}
			return;
		}
		case ConnectionNodeType.EntryPointCaller: {
			if (
				ENTRY_POINT_TYPES.includes(sourceEntity.type) &&
				CALLABLE_TYPES.includes(targetEntity.type)
			) {
				(sourceEntity as EntryPointEntityState).addCall(
					targetEntity as CallableEntityState,
					changeSet
				);

				(targetEntity as CallableEntityState).addEntryCaller(
					sourceEntity as EntryPointEntityState,
					changeSet,
					true
				);
				changeSet.actionName === StateMutationAction.ConnectExecution;

				// If the target is an internal call, move all the callers of the parent variable to the internal call
				if (targetEntity.type === EntityType.InternalCall) {
					const parentVariable = (targetEntity as InternalCallState)
						.parent as VariableState;

					[...parentVariable.calledByEntry].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addEntryCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledBySuccess].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addSuccessCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addSuccessCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledByError].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addErrorCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addErrorCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
				}
			}
			return;
		}
		case ConnectionNodeType.SuccessCaller: {
			if (
				PASS_THROUGH_CALLABLE_TYPES.includes(sourceEntity.type) &&
				CALLABLE_TYPES.includes(targetEntity.type)
			) {
				(sourceEntity as PassThroughCallableEntityState).addSuccessCall(
					targetEntity as CallableEntityState,
					changeSet,
					true
				);

				(targetEntity as CallableEntityState).addSuccessCaller(
					sourceEntity as PassThroughCallableEntityState,
					changeSet,
					true
				);
				changeSet.actionName === StateMutationAction.ConnectExecution;

				// If the target is an internal call, move all the callers of the parent variable to the internal call
				if (targetEntity.type === EntityType.InternalCall) {
					const parentVariable = (targetEntity as InternalCallState)
						.parent as VariableState;

					[...parentVariable.calledByEntry].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addEntryCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledBySuccess].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addSuccessCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addSuccessCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledByError].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addErrorCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addErrorCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
				}
			}
			return;
		}
		case ConnectionNodeType.ErrorCaller: {
			if (
				PASS_THROUGH_CALLABLE_TYPES.includes(sourceEntity.type) &&
				CALLABLE_TYPES.includes(targetEntity.type)
			) {
				(sourceEntity as PassThroughCallableEntityState).addErrorCall(
					targetEntity as CallableEntityState,
					changeSet,
					true
				);

				(targetEntity as CallableEntityState).addErrorCaller(
					sourceEntity as PassThroughCallableEntityState,
					changeSet,
					true
				);
				changeSet.actionName === StateMutationAction.ConnectExecution;

				// If the target is an internal call, move all the callers of the parent variable to the internal call
				if (targetEntity.type === EntityType.InternalCall) {
					const parentVariable = (targetEntity as InternalCallState)
						.parent as VariableState;

					[...parentVariable.calledByEntry].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addEntryCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as EntryPointEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledBySuccess].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addSuccessCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addSuccessCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
					[...parentVariable.calledByError].forEach((caller) => {
						// If the caller is not already in the list, add it
						caller.addErrorCall(
							targetEntity as CallableEntityState,
							changeSet
						);
						(targetEntity as InternalCallState).addErrorCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
						parentVariable.removeCaller(
							caller as PassThroughCallableEntityState,
							changeSet,
							true
						);
					});
				}
			}
			return;
		}
	}
}

export function entitiesToUniqueCanvasEntities(
	entities: EntityState[],
	filterOut: string[] = []
): CanvasEntityState[] {
	const canvasEntities: CanvasEntityState[] = filterOutDuplicateEntities(
		getParentCanvasEntities(entities)
	).filter((entity) => {
		if (!filterOut.includes(entity.id)) {
			return true;
		}

		return false;
	}) as CanvasEntityState[];

	return canvasEntities;
}

// Removes any non-canvas entities in the list
export function filterOutNonCanvasEntities(
	entities: EntityState[]
): CanvasEntityState[] {
	const canvasEntities: CanvasEntityState[] = (
		filterOutDuplicateEntities(entities) as EntityState[]
	).filter((entity) => {
		const parentCanvasEntity = getParentCanvasEntity(entity);

		if (parentCanvasEntity.id === entity.id) {
			return true;
		}

		return false;
	}) as CanvasEntityState[];

	return canvasEntities;
}

export function entitiesToUniqueCanvasElements(
	entities: EntityState[],
	project: EditorService,
	filterOut: string[] = []
): CanvasEntityElement[] {
	const canvasElements: CanvasEntityElement[] = filterOutDuplicateEntities(
		getParentCanvasEntities(entities)
	).reduce((acc, entity) => {
		const canvasElement = project.getElement(entity.id);

		if (canvasElement && !filterOut.includes(entity.id)) {
			return [...acc, canvasElement];
		}

		return acc;
	}, []);

	return canvasElements;
}

// Returns the assembled browser path that opens the database client dialog for a definition entity
// This takes the definition entity, finds which database it belongs to and assebles the path
export function resolvePersistedDefinitionEntityDatabaseDialogAbsolutePath(
	project: EditorService,
	entity: DefinitionEntityState,
	mode?: TestDatabaseDialogMode
): string | null {
	const databaseEntity =
		resolvePersistedDefinitionEntityDatabaseEntity(entity);

	if (!databaseEntity) {
		return null;
	}

	const databaseEntityId = databaseEntity.id;

	let path = `${project.editorBasePath}/test-relational-database/${databaseEntityId}/table/${entity.id}`;

	if (mode) {
		path += `?mode=${mode}`;
	}

	return path;
}

// This function returns the entities that can be selected as options on an un-contrained data type selector field
// Some data types are constrained so that only certain entities can be selected, but this function returns all entities
export function getAllDataTypeBuilderOptions(
	project: ProjectState,
	parent: UserManagedDataTypeParentEntityState
): DataTypeBuilderOption[] {
	const remainingPrimitives = project.primitives.filter((primitive) => {
		if (
			BUILT_IN_BASE_ENTITY_IDS[EntityType.PrimitiveEntity].void.id ===
			primitive.id
		) {
			if (
				[EntityType.FunctionDeclaration, ...TERMINATION_TYPES].includes(
					parent.type
				)
			) {
				return true;
			} else {
				return false;
			}
		}

		return true;
	});

	let options: DataTypeBuilderOption[] = [
		...remainingPrimitives,

		...project.entities,

		// Remove all non-interactive base entities
		...project.builtInBaseEntities //.filter(ent => ent.interactive),

		// ...project.functions
	].filter(
		(ent) =>
			!(ENTITIES_METADATA_JSON as Record<string, any>)[ent.id]?.hidden
	);

	if (
		parent.type === EntityType.Property &&
		parent.parent.type !== EntityType.PrimitiveEntity &&
		checkHasBaseEntity(parent.parent, BaseEntityNames.PERSISTED_ENTITY)
	) {
		const ownDatabase = resolvePersistedDefinitionEntityDatabaseEntity(
			parent.parent as DefinitionEntityState
		);

		if (!ownDatabase) {
			return options;
		}

		const allUniquePropertiesOfPersistedEntities = project.entities
			.filter((ent) => {
				const databaseOfEntity =
					resolvePersistedDefinitionEntityDatabaseEntity(
						ent as DefinitionEntityState
					);

				return (
					databaseOfEntity?.id === ownDatabase.id &&
					checkHasBaseEntity(ent, BaseEntityNames.PERSISTED_ENTITY)
				);
			})
			.reduce((acc, entity) => {
				const uniqueProperties = entity.properties.filter(
					(prop) => prop.unique && prop.id !== parent.id
				);

				return [...acc, ...uniqueProperties];
			}, [] as PropertyState[]);

		options = [
			...(filterOutDuplicateEntities(
				allUniquePropertiesOfPersistedEntities
			) as PropertyState[]),
			...options
		];
	}

	return options;
}

export function getPrimitiveTypeInputs(
	entity: PassThroughCallableEntityState,
	allowed: PrimitiveTypes[] = Object.values(PrimitiveTypes)
): InputMapState[] {
	if (!entity) {
		return [];
	}

	const inputs = ((entity.inputs || []) as InputMapState[]).filter(
		(input: InputMapState) => {
			const inputDataType = input.getDataType(null);

			if (inputDataType?.entity?.type === EntityType.PrimitiveEntity) {
				const primitiveName = inputDataType?.entity
					?.name as PrimitiveTypes;

				return allowed.includes(primitiveName as PrimitiveTypes);
			}

			return false;
		}
	);

	return inputs;
}

export const initializeNewInputDeclarationForSearch = (
	project: EditorService,
	parent: SearchState,
	baseName = 'External input',
	existingChangeSet?: ChangeSet
): { declaration: ValueDescriptorState; inputMap: ActionInputMapState } => {
	const changeSet =
		existingChangeSet ||
		project.logic.addChangeSet(
			new ChangeSet(
				project.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				parent,
				false,
				StateMutationAction.AddInputToSearch
			)
		);

	const newEntityTransfer = ValueDescriptorState.new(changeSet);
	newEntityTransfer.parent = parent.toReference();
	newEntityTransfer.index = parent.inputs.length;
	newEntityTransfer.parentRelationType =
		ValueDescriptorParentChildRelation.FixedArguments;
	newEntityTransfer.name = findNextAvailableName(
		baseName,
		parent.inputsDeclarations
	);
	newEntityTransfer.required = true;

	const numberOfEquivalentEntitiesWithSameName = parent.inputs.filter(
		(ent) => ent.name === newEntityTransfer.name
	).length;

	if (numberOfEquivalentEntitiesWithSameName > 0) {
		newEntityTransfer.name += ` ${numberOfEquivalentEntitiesWithSameName}`;
	}

	const newEntity = createStateFromTypeAndSubscribe(
		newEntityTransfer,
		project.logic
	) as ValueDescriptorState;

	newEntity.hydrateAncestors();

	changeSet.add(newEntity, ChangeSetEntityChangeType.Added);

	newEntity.addSelfToProject(changeSet);
	newEntity.initChildren(changeSet);

	changeSet.add(parent, ChangeSetEntityChangeType.Affected);

	const newDefaultDataTypeTransfer = DataTypeState.new(changeSet);
	newDefaultDataTypeTransfer.parent = newEntity.toReference();

	const newDefaultDataType = createStateFromTypeAndSubscribe(
		newDefaultDataTypeTransfer,
		project.logic
	) as DataTypeState;

	newDefaultDataType.hydrateAncestors();

	changeSet.add(newDefaultDataType, ChangeSetEntityChangeType.Added);

	newDefaultDataType.addSelfToProject(changeSet);
	newDefaultDataType.initChildren(changeSet);

	newDefaultDataType.setEntity(
		project.logic.getBuiltIn(
			BUILT_IN_BASE_ENTITY_IDS['primitive-entity'].string.id
		) as PrimitiveEntityState,
		changeSet
	);

	changeSet.add(newEntity, ChangeSetEntityChangeType.Affected);

	// Generate any missing counterpart input-maps from the new input-declaration
	parent.afterAllChildrenInitialized(changeSet);

	if (!existingChangeSet) {
		project.renderAndCloseChangeSet(changeSet);
	}

	return {
		declaration: newEntity,
		inputMap: parent.inputs.find(
			(inputMap) => inputMap.declaration?.id === newEntity.id
		) as ActionInputMapState
	};
};

export function modifyEditableAndDeletableDownstreamEntitiesFromGiven(
	entity: UserManagedEntityState,
	changeSet: ChangeSet,
	newValue: boolean,
	seenEntities = new Set<EntityId>()
) {
	if (
		seenEntities.has(entity.id) ||
		entity.project.diggestedBuiltInBaseEntitiesIds.has(entity.id)
	) {
		return;
	}

	seenEntities.add(entity.id);

	entity.editable = newValue;
	entity.deletable = newValue;

	changeSet.add(entity, ChangeSetEntityChangeType.Updated);

	const { downstream } = getReferencedIds(entity, entity.project.instances, {
		ignoreBuiltInBaseEntities: true,
		allBuiltInEntityIds: entity.project.diggestedBuiltInBaseEntitiesIds
	});

	Object.keys(downstream).forEach((downstreamId) => {
		const downstreamEntity = entity.project.get(
			downstreamId
		) as UserManagedEntityState;

		if (downstreamEntity) {
			modifyEditableAndDeletableDownstreamEntitiesFromGiven(
				downstreamEntity,
				changeSet,
				newValue,
				seenEntities
			);
		}
	});
}

export function freezeDownstreamEntitiesFromGiven(
	entity: UserManagedEntityState,
	changeSet: ChangeSet,
	seenEntities = new Set<EntityId>()
) {
	modifyEditableAndDeletableDownstreamEntitiesFromGiven(
		entity,
		changeSet,
		true,
		seenEntities
	);
}

export function unfreezeDownstreamEntitiesFromGiven(
	entity: UserManagedEntityState,
	changeSet: ChangeSet,
	seenEntities = new Set<EntityId>()
) {
	modifyEditableAndDeletableDownstreamEntitiesFromGiven(
		entity,
		changeSet,
		false,
		seenEntities
	);
}

export function resolveEarliestSingleCallerAncestor(
	entity: CallableEntityState
): {
	ancestor: CallerEntityState | null;
	callType: 'success' | 'error' | 'entry' | 'loop-body' | null;
} {
	const flatCallers = flattenCallers(entity);

	if (!flatCallers.length) {
		return {
			ancestor: null,
			callType: null
		};
	}

	// Filter out all callers that are not single common ancestors
	const reaminingCallsThatReachTarget = flatCallers
		.filter((flatCall) => {
			if (flatCall === entity) {
				return false;
			}

			if (!CALLER_TYPES.includes(flatCall.type)) {
				return false;
			}

			const flatCalls = flattenElementCalls(
				flatCall as CallerEntityState
			);

			const containsTarget = flatCalls.find(
				(flatCall) => flatCall === entity
			);

			if (!containsTarget) {
				return false;
			}

			// If the flat call is a caller, we return it
			return true;
		})
		.filter(
			// Filter out duplicate callers
			(flatCall, index, self) =>
				self.findIndex((c) => c === flatCall) === index
		);

	const callsWhereAllTargetCallersHaveAsAncestor =
		reaminingCallsThatReachTarget.filter((flatCall) => {
			const allLeadTo = checkEveryCallerReachesOrIsAncestor(
				entity,
				flatCall as CallerEntityState
			);

			return allLeadTo;
		});

	// Remove dependent callers
	const filteredCalls = callsWhereAllTargetCallersHaveAsAncestor.filter(
		(callInfo) => {
			const flatCalls = flattenElementCalls(
				callInfo as CallerEntityState
			);

			const otherCalls = callsWhereAllTargetCallersHaveAsAncestor
				.filter((otherCallInfo) => otherCallInfo !== callInfo)
				.map((otherCallInfo) => otherCallInfo as CallerEntityState);

			const anyIsDependent = flatCalls.some((flatCall) => {
				const found = otherCalls.find(
					(otherCall) => otherCall === flatCall
				);

				return !!found;
			});

			return !anyIsDependent;
		}
	);

	if (filteredCalls.length === 0) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (filteredCalls.length === 1) {
		const singleCall = filteredCalls[0];

		if (singleCall.type === EntityType.Loop) {
			if (singleCall.body.calls.includes(entity)) {
				return {
					ancestor: singleCall as CallerEntityState,
					callType: 'loop-body'
				};
			}
		} else if (ENTRY_POINT_TYPES.includes(singleCall.type)) {
			if ((singleCall as EntryPointEntityState).calls.includes(entity)) {
				if (
					singleCall.type === EntityType.FunctionDeclaration &&
					singleCall.parent.type === EntityType.Loop
				) {
					return {
						ancestor: singleCall as CallerEntityState,
						callType: 'loop-body'
					};
				} else {
					return {
						ancestor: singleCall as CallerEntityState,
						callType: 'entry'
					};
				}
			}
		} else if (PASS_THROUGH_CALLABLE_TYPES.includes(singleCall.type)) {
			if (
				(
					singleCall as PassThroughCallableEntityState
				).successCalls.includes(entity)
			) {
				return {
					ancestor: singleCall as CallerEntityState,
					callType: 'success'
				};
			} else if (
				(
					singleCall as PassThroughCallableEntityState
				).errorCalls.includes(entity)
			) {
				return {
					ancestor: singleCall as CallerEntityState,
					callType: 'error'
				};
			}
		}
	}

	// If there are multiple callers throw an error
	if (filteredCalls.length > 1) {
		throw new Error(
			`resolveEarliestSingleCallerAncestor: Multiple callers found for entity ${entity.id}.`
		);
	}

	return {
		ancestor: filteredCalls[0] as CallerEntityState,
		callType: null
	};
}

export function findNextCombinedSingleCallerAncestor(
	targetA: CallableEntityState | CallerEntityState,
	targetB: CallableEntityState | CallerEntityState,
	from?: CallerEntityState
): {
	ancestor: CallerEntityState | null;
	callType: 'success' | 'error' | 'entry' | 'loop-body' | null;
} {
	const result: {
		ancestor: CallerEntityState | null;
		callType: 'success' | 'error' | 'entry' | 'loop-body' | null;
	} = {
		ancestor: null,
		callType: null
	};

	if (!targetA) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] No 'targetA' given`
		);
	}

	if (!targetB) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] No 'targetB' given`
		);
	}

	if (!!from && !CALLER_TYPES.includes(from.type)) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] Invalid 'from' argument, is not a caller entity: ${from.type} -- ${from.id}`
		);
	}

	if (targetA?.id === targetB?.id) {
		if (CALLER_TYPES.includes(targetA.type)) {
			let callType: 'success' | 'error' | 'entry' | 'loop-body' | null =
				null;
			if (
				targetA.type === EntityType.Loop ||
				(targetA.type === EntityType.FunctionDeclaration &&
					targetA.parent.type === EntityType.Loop)
			) {
				// If the entry point is a function declaration inside a loop, we need to move to the loop body
				callType = 'loop-body';
			} else if (
				targetA.type === EntityType.FunctionDeclaration ||
				targetA.type === EntityType.GlobalEvent
			) {
				callType = 'entry';
			}

			return {
				ancestor: targetA as CallerEntityState,
				callType: callType || 'success'
			};
		} else if (CALLABLE_TYPES.includes(targetA.type)) {
			const ownResult = findNextSingleCallerAncestor(
				targetA as CallableEntityState,
				from
			);

			return ownResult;
		} else {
			throw new Error(
				`[findNextCombinedSingleCallerAncestor] Invalid 'targetA' and 'targetB' arguments, neither is a callable or caller entity: ${targetA.type} -- ${targetA.id}`
			);
		}
	}

	if (!!from) {
		const flatCallsResult = flattenElementCallsWithCallType(from);

		const containsTargetA = flatCallsResult.find(
			(flatCall) => flatCall.callee === targetA
		);
		const containsTargetB = flatCallsResult.find(
			(flatCall) => flatCall.callee === targetB
		);

		if (!containsTargetA) {
			throw new Error(
				`[findNextCombinedSingleCallerAncestor] Invalid 'from' ${from.type} -- ${from.id} argument, doesn't contain 'targetA' entity ${targetA.type} -- ${targetA.id}`
			);
		}

		if (!containsTargetB) {
			throw new Error(
				`[findNextCombinedSingleCallerAncestor] Invalid 'from' ${from.type} -- ${from.id} argument, doesn't contain 'targetB' entity ${targetB.type} -- ${targetB.id}`
			);
		}
	}

	const rootEntryPointA = getRootEntryPointScopeOwner(
		targetA,
		targetA.project
	);
	const rootEntryPointB = getRootEntryPointScopeOwner(
		targetB,
		targetB.project
	);

	let startPoint: CallerEntityState | null = from || null;

	if (!startPoint && rootEntryPointA?.id === rootEntryPointB?.id) {
		startPoint = rootEntryPointA;
	}

	const scopeOwnerA = getScopeOwner(targetA, targetA.project);
	const scopeOwnerB = getScopeOwner(targetB, targetB.project);

	if (!scopeOwnerA) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] Invalid 'targetA' argument, doesn't have a scope owner: ${targetA.type} -- ${targetA.id}`
		);
	}

	if (!scopeOwnerB) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] Invalid 'targetB' argument, doesn't have a scope owner: ${targetB.type} -- ${targetB.id}`
		);
	}

	let areRelated: boolean = false;

	const scopeCompatibility = checkScopeCompatibility(
		targetA,
		targetB,
		targetA.project
	);

	if (!scopeCompatibility.compatible) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (
		scopeCompatibility.commonAncestorScope.type !== EntityType.Project &&
		!startPoint
	) {
		startPoint = scopeCompatibility.commonAncestorScope;
	}

	// One must call the other, or any of the ancestors of one must call the other
	const targetAFlatCalls = TERMINATION_TYPES.includes(targetA.type)
		? []
		: flattenElementCalls(targetA as CallerEntityState);

	const targetACallsB = targetAFlatCalls.find(
		(flatCall) => flatCall.id === targetB.id
	);

	if (targetACallsB) {
		areRelated = true;
	}

	const targetACallers = CALLABLE_TYPES.includes(targetA.type)
		? flattenCallers(targetA as CallableEntityState)
		: [];

	if (!areRelated) {
		const ancestorCallsB = targetACallers.find((caller) => {
			const flatCalls = flattenElementCallsWithCallType(caller);

			return flatCalls.find(
				(flatCall) => flatCall.callee.id === targetB.id
			);
		});

		areRelated = !!ancestorCallsB;
	}

	if (!startPoint) {
		// Pick the oldest ancestor
		// The one that calls others
		let oldestAncestor = targetACallers[0];

		for (let i = 1; i < targetACallers.length; i++) {
			const caller = targetACallers[i];

			const flatCalls = flattenElementCallsWithCallType(caller);

			const callsCurOldest = flatCalls.find((flatCall) => {
				return oldestAncestor?.id === flatCall.callee.id;
			});

			if (callsCurOldest) {
				oldestAncestor = caller;
			}
		}

		startPoint = oldestAncestor;
	}

	if (!areRelated) {
		const targetBFlatCalls = TERMINATION_TYPES.includes(targetB.type)
			? []
			: flattenElementCalls(targetB as CallerEntityState);

		const targetBCallsA = targetBFlatCalls.find(
			(flatCall) => flatCall.id === targetA.id
		);

		if (targetBCallsA) {
			areRelated = true;
		}
	}

	const targetBCallers = CALLABLE_TYPES.includes(targetB.type)
		? flattenCallers(targetB as CallableEntityState)
		: [];

	if (!areRelated) {
		const ancestorCallsA = targetBCallers.find((caller) => {
			const flatCalls = flattenElementCallsWithCallType(caller);

			return flatCalls.find(
				(flatCall) => flatCall.callee.id === targetA.id
			);
		});

		areRelated = !!ancestorCallsA;
	}

	if (!startPoint && areRelated) {
		// Pick the oldest ancestor
		// The one that calls others
		let oldestAncestor = targetBCallers[0];

		for (let i = 1; i < targetBCallers.length; i++) {
			const caller = targetBCallers[i];

			const flatCalls = flattenElementCallsWithCallType(caller);

			const callsCurOldest = flatCalls.find((flatCall) => {
				return oldestAncestor?.id === flatCall.callee.id;
			});

			if (callsCurOldest) {
				oldestAncestor = caller;
			}
		}

		startPoint = oldestAncestor;
	}

	if (!areRelated) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (!startPoint) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (!CALLER_TYPES.includes(startPoint.type)) {
		throw new Error(
			`[findNextCombinedSingleCallerAncestor] Invalid 'from' argument, is not a caller entity: ${startPoint.type} -- ${startPoint.id}`
		);
	}

	// Check if any of the targets is the starting point
	if (startPoint.id === targetA.id) {
		const aEarliestCalls = flattenElementCallsWithCallType(
			targetB as CallerEntityState
		);

		const callsTargetA = aEarliestCalls.find(
			(flatCall) => flatCall.callee.id === targetA?.id
		);

		if (callsTargetA) {
			return {
				ancestor: startPoint,
				callType: callsTargetA.callType
			};
		} else {
			if (CALLER_TYPES.includes(targetA.type)) {
				let callType:
					| 'success'
					| 'error'
					| 'entry'
					| 'loop-body'
					| null = null;
				if (
					targetA.type === EntityType.Loop ||
					(targetA.type === EntityType.FunctionDeclaration &&
						targetA.parent.type === EntityType.Loop)
				) {
					// If the entry point is a function declaration inside a loop, we need to move to the loop body
					callType = 'loop-body';
				} else if (
					targetA.type === EntityType.FunctionDeclaration ||
					targetA.type === EntityType.GlobalEvent
				) {
					callType = 'entry';
				}

				return {
					ancestor: targetA as CallerEntityState,
					callType: callType || 'success'
				};
			} else if (CALLABLE_TYPES.includes(targetA.type)) {
				const ownResult = findNextSingleCallerAncestor(
					targetA as CallableEntityState,
					from
				);

				return ownResult;
			}

			return {
				ancestor: null,
				callType: null
			};
		}
	}

	if (startPoint.id === targetB.id) {
		const bEarliestCalls = flattenElementCallsWithCallType(
			targetA as CallerEntityState
		);

		const callsTargetB = bEarliestCalls.find(
			(flatCall) => flatCall.callee.id === targetB?.id
		);

		if (callsTargetB) {
			return {
				ancestor: startPoint,
				callType: callsTargetB.callType
			};
		} else {
			if (CALLER_TYPES.includes(targetB.type)) {
				let callType:
					| 'success'
					| 'error'
					| 'entry'
					| 'loop-body'
					| null = null;
				if (
					targetB.type === EntityType.Loop ||
					(targetB.type === EntityType.FunctionDeclaration &&
						targetB.parent.type === EntityType.Loop)
				) {
					// If the entry point is a function declaration inside a loop, we need to move to the loop body
					callType = 'loop-body';
				} else if (
					targetB.type === EntityType.FunctionDeclaration ||
					targetB.type === EntityType.GlobalEvent
				) {
					callType = 'entry';
				}

				return {
					ancestor: targetB as CallerEntityState,
					callType: callType || 'success'
				};
			} else if (CALLABLE_TYPES.includes(targetB.type)) {
				const ownResult = findNextSingleCallerAncestor(
					targetB as CallableEntityState,
					from
				);

				return ownResult;
			}

			return {
				ancestor: null,
				callType: null
			};
		}
	}

	if (
		CALLABLE_TYPES.includes(targetA.type) &&
		CALLABLE_TYPES.includes(targetB.type)
	) {
		const earliestSingleOfA = findNextSingleCallerAncestor(
			targetA as CallableEntityState,
			startPoint
		);
		const earliestSingleOfB = findNextSingleCallerAncestor(
			targetB as CallableEntityState,
			startPoint
		);

		// Here, the problem is that the earliestSingleOfReaders might not also be the current entity's single caller ancestor
		// So next, we need to check the earliest single ancestor of the combined 'earliestSingleOfReaders.ancestor' and own 'result.ancestor'
		const aEarliestCalls = flattenElementCallsWithCallType(
			earliestSingleOfA.ancestor as CallerEntityState
		);

		const bEarliestCalls = flattenElementCallsWithCallType(
			earliestSingleOfB.ancestor as CallerEntityState
		);

		if (earliestSingleOfB.ancestor?.id === earliestSingleOfA.ancestor?.id) {
			// If they are the same, we can use it directly
			result.ancestor = earliestSingleOfB.ancestor;
			result.callType = earliestSingleOfB.callType;
		} else if (earliestSingleOfB.ancestor?.id === targetA.id) {
			// If the earliest single of B is targetA, we can use it directly
			result.ancestor = earliestSingleOfB.ancestor;
			result.callType = earliestSingleOfB.callType;
		} else if (earliestSingleOfA.ancestor?.id === targetB.id) {
			// If the earliest single of A is targetB, we can use it directly
			result.ancestor = earliestSingleOfA.ancestor;
			result.callType = earliestSingleOfA.callType;
		} else if (
			aEarliestCalls.some((call) => call.callee.id === targetB?.id)
		) {
			// If targetB is found within the calls of A's ancestor, it means, A's ancestor is also calling B
			// We recusrively repeat, with a new starting point
			// TODO finish this
			if (
				startPoint?.type === EntityType.Loop ||
				(startPoint?.type === EntityType.FunctionDeclaration &&
					startPoint?.parent.type === EntityType.Loop)
			) {
				// If the entry point is a function declaration inside a loop, we need to move to the loop body
				result.callType = 'loop-body';
			} else if (
				startPoint?.type === EntityType.FunctionDeclaration ||
				startPoint?.type === EntityType.GlobalEvent
			) {
				result.callType = 'entry';
			} else {
				const flatCalls = flattenElementCallsWithCallType(
					startPoint as CallerEntityState
				);
				const callsTargetA = flatCalls.find(
					(flatCall) => flatCall.callee.id === targetA?.id
				);

				return {
					ancestor: startPoint as CallerEntityState,
					callType: callsTargetA?.callType || 'success'
				};
			}
			result.ancestor = startPoint;
		} else if (
			bEarliestCalls.some((call) => call.callee.id === targetA?.id)
		) {
			if (
				startPoint?.type === EntityType.Loop ||
				(startPoint?.type === EntityType.FunctionDeclaration &&
					startPoint?.parent.type === EntityType.Loop)
			) {
				// If the entry point is a function declaration inside a loop, we need to move to the loop body
				result.callType = 'loop-body';
			} else if (
				startPoint?.type === EntityType.FunctionDeclaration ||
				startPoint?.type === EntityType.GlobalEvent
			) {
				result.callType = 'entry';
			} else {
				const flatCalls = flattenElementCallsWithCallType(
					startPoint as CallerEntityState
				);
				const callsTargetA = flatCalls.find(
					(flatCall) => flatCall.callee.id === targetB?.id
				);

				return {
					ancestor: startPoint as CallerEntityState,
					callType: callsTargetA?.callType || 'success'
				};
			}
			result.ancestor = startPoint;
		} else {
			if (
				startPoint?.type === EntityType.Loop ||
				(startPoint?.type === EntityType.FunctionDeclaration &&
					startPoint?.parent.type === EntityType.Loop)
			) {
				// If the entry point is a function declaration inside a loop, we need to move to the loop body
				result.callType = 'loop-body';
			} else if (
				startPoint?.type === EntityType.FunctionDeclaration ||
				startPoint?.type === EntityType.GlobalEvent
			) {
				result.callType = 'entry';
			} else {
				const flatCalls = flattenElementCallsWithCallType(
					startPoint as CallerEntityState
				);
				const callsTargetA = flatCalls.find(
					(flatCall) => flatCall.callee.id === targetA?.id
				);

				return {
					ancestor: startPoint as CallerEntityState,
					callType: callsTargetA?.callType || 'success'
				};
			}
			result.ancestor = startPoint;
		}
	}

	if (!result.ancestor) {
		let callType: 'success' | 'error' | 'entry' | 'loop-body' | null = null;

		if (
			startPoint?.type === EntityType.Loop ||
			(startPoint?.type === EntityType.FunctionDeclaration &&
				startPoint?.parent.type === EntityType.Loop)
		) {
			// If the entry point is a function declaration inside a loop, we need to move to the loop body
			callType = 'loop-body';
		} else if (
			startPoint?.type === EntityType.FunctionDeclaration ||
			startPoint?.type === EntityType.GlobalEvent
		) {
			callType = 'entry';
		}

		return {
			ancestor: startPoint as CallerEntityState,
			callType: callType || 'success'
		};
	}

	return result;
}

export function findNextSingleCallerAncestor(
	target: CallableEntityState | EntryPointEntityState,
	from?: CallerEntityState,
	debug: boolean = false
): {
	ancestor: CallerEntityState | null;
	callType: 'success' | 'error' | 'entry' | 'loop-body' | null;
} {
	if (!target) {
		throw new Error(
			'findNextSingleCallerAncestor: Target is not defined, cannot find ancestor.'
		);
	}

	if (
		target.type === EntityType.FunctionDeclaration ||
		target.type === EntityType.GlobalEvent
	) {
		return {
			ancestor: target,
			callType: 'entry'
		};
	}

	let sanitizedTarget: CallableEntityState = target;

	if (
		(target.type === EntityType.VariableDeclaration ||
			target.type === EntityType.VariableInstance) &&
		!target.calledBy.length &&
		!target.successCalls.length &&
		!target.errorCalls.length &&
		target.internalCalls.some(
			(internalCall) => !!internalCall.calledBy.length
		)
	) {
		const usedInternalCall = target.internalCalls.find(
			(internalCall) => !!internalCall.calledBy.length
		);
		// If the target is an internal call, we need to use the parent variable as the target
		sanitizedTarget = usedInternalCall as CallableEntityState;
	}

	const targetScopeOwner = getScopeOwner(sanitizedTarget, target.project) as
		| EntryPointEntityState
		| ProjectState;
	let startPoint = from || targetScopeOwner;

	if (!startPoint) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (startPoint.type === EntityType.Project) {
		startPoint = resolveEarliestSingleCallerAncestor(sanitizedTarget)
			.ancestor as CallerEntityState;

		if (!startPoint) {
			return {
				ancestor: null,
				callType: null
			};
		}
	}

	const flatCallsResult = flattenElementCallsWithCallType(startPoint);

	const containsTarget = flatCallsResult.find(
		(flatCall) => flatCall.callee?.id === sanitizedTarget?.id
	);

	if (!containsTarget) {
		return {
			ancestor: null,
			callType: null
		};
	}

	const inmediateCallersOfTarget = [...sanitizedTarget.calledBy];

	if (!inmediateCallersOfTarget.length) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (inmediateCallersOfTarget.length === 1) {
		const singleCaller = inmediateCallersOfTarget[0];

		if (singleCaller.type === EntityType.Loop) {
			if (singleCaller.body.calls.includes(sanitizedTarget)) {
				return {
					ancestor: singleCaller as CallerEntityState,
					callType: 'loop-body'
				};
			}
		}

		if (ENTRY_POINT_TYPES.includes(singleCaller.type)) {
			if (
				singleCaller.type === EntityType.FunctionDeclaration &&
				singleCaller.parent.type === EntityType.Loop
			) {
				return {
					ancestor: singleCaller.parent as CallerEntityState,
					callType: 'loop-body'
				};
			} else {
				return {
					ancestor: singleCaller as CallerEntityState,
					callType: 'entry'
				};
			}
		} else if (PASS_THROUGH_CALLABLE_TYPES.includes(singleCaller.type)) {
			if (
				(
					singleCaller as PassThroughCallableEntityState
				).successCalls.includes(sanitizedTarget)
			) {
				return {
					ancestor: singleCaller as CallerEntityState,
					callType: 'success'
				};
			} else if (
				(
					singleCaller as PassThroughCallableEntityState
				).errorCalls.includes(sanitizedTarget)
			) {
				return {
					ancestor: singleCaller as CallerEntityState,
					callType: 'error'
				};
			}
		}
	}

	const targetFlatCalls = TERMINATION_TYPES.includes(sanitizedTarget.type)
		? []
		: flattenElementCalls(sanitizedTarget as CallerEntityState);

	const flatCallsUntilTarget = flatCallsResult.filter((flatCall) => {
		if (TERMINATION_TYPES.includes(flatCall.callee.type)) {
			return false;
		}

		if (flatCall.callee === sanitizedTarget) {
			return false;
		}

		const flatCallCalls = flattenElementCalls(
			flatCall.callee as CallerEntityState
		);

		if (!flatCallCalls.find((call) => call?.id === sanitizedTarget.id)) {
			return false;
		}

		if (targetFlatCalls.find((call) => call?.id === flatCall.callee.id)) {
			return false;
		}

		return true;
	});

	/*
		1. Check if the start point directly calls the target, then return start point as the ancestor if:
				- It is an entry point
				- Or if it is the only caller of the target

		2. Otherwise from the start point, flatten calls only until the target ('flatCallsUntilTarget').
				Meaning, if any calls are also descendant of the target, don't include them

		3. For all flat calls, check if they are callers of the target, if not, ignore them

		4. Get the immediate callers of the target

		5. For each of the remaining flat callers, check if it calls all the immediate callers of the target or the target itself
				- If yes, keep it
				- If no, ignore it

		6. If there are multiple callers left, reduce their combined next single caller ancestor
	*/

	if (debug) {
		Logger.log(
			'Flat calls from start point',
			startPoint.type + ' -- ' + startPoint.id,
			flatCallsResult.length,
			flatCallsResult.map(
				(c, i) =>
					i +
					1 +
					'. "' +
					resolveEntityName(c.callee, target.project) +
					'" ' +
					c.callee.type +
					' -- ' +
					c.callee.id
			)
		);
		Logger.log('----------------------------------------');
	}

	if (debug) {
		Logger.log(
			'Relevant flat calls from start point',
			startPoint.type + ' -- ' + startPoint.id,
			flatCallsUntilTarget.length,
			flatCallsUntilTarget.map(
				(c, i) =>
					i +
					1 +
					'. "' +
					resolveEntityName(c.callee, target.project) +
					'" ' +
					c.callee.type +
					' -- ' +
					c.callee.id
			)
		);
		Logger.log('----------------------------------------');
	}

	// Check if the start point directly calls the target
	if (startPoint.type === EntityType.Loop) {
		if (startPoint.body.calls.includes(sanitizedTarget)) {
			return {
				ancestor: startPoint as CallerEntityState,
				callType: 'loop-body'
			};
		}
	} else if (ENTRY_POINT_TYPES.includes(startPoint.type)) {
		if (
			(startPoint as EntryPointEntityState).calls.includes(
				sanitizedTarget
			)
		) {
			if (
				startPoint.type === EntityType.FunctionDeclaration &&
				startPoint.parent.type === EntityType.Loop
			) {
				return {
					ancestor: startPoint.parent as CallerEntityState,
					callType: 'loop-body'
				};
			} else {
				return {
					ancestor: startPoint as CallerEntityState,
					callType: 'entry'
				};
			}
		}
	} else if (PASS_THROUGH_CALLABLE_TYPES.includes(startPoint.type)) {
		if (sanitizedTarget.calledBy.length === 1) {
			if (
				(
					startPoint as PassThroughCallableEntityState
				).successCalls.includes(sanitizedTarget)
			) {
				return {
					ancestor: startPoint as CallerEntityState,
					callType: 'success'
				};
			} else if (
				(
					startPoint as PassThroughCallableEntityState
				).errorCalls.includes(sanitizedTarget)
			) {
				return {
					ancestor: startPoint as CallerEntityState,
					callType: 'error'
				};
			}
		}
	}

	const reaminingCallsThatReachTarget = flatCallsUntilTarget
		.filter((flatCall) => {
			if (flatCall.callee === sanitizedTarget) {
				return false;
			}

			if (!CALLER_TYPES.includes(flatCall.callee.type)) {
				return false;
			}

			const isInmediateCaller = !!inmediateCallersOfTarget.find(
				(caller) => caller === flatCall.callee
			);

			if (isInmediateCaller) {
				return true;
			}

			const flatCalls = flattenElementCalls(
				flatCall.callee as CallerEntityState
			);

			const containsTarget = !!flatCalls.find(
				(flatCall) => flatCall === sanitizedTarget
			);

			const containsAllInmediateCallers =
				!!inmediateCallersOfTarget.every((caller) =>
					flatCalls.find((flatCall) => flatCall === caller)
				);

			if (!containsTarget) {
				return false;
			}

			if (!containsAllInmediateCallers) {
				return false;
			}

			// If the flat call is a caller, we return it
			return true;
		})
		.filter(
			// Filter out duplicate callers
			(flatCall, index, self) =>
				self.findIndex((c) => c.callee === flatCall.callee) === index
		) as {
		callee: CallerEntityState;
		callType: 'success' | 'error' | 'entry' | 'loop-body';
	}[];

	if (debug) {
		Logger.log(
			'Remaining calls that reach target',
			reaminingCallsThatReachTarget.length,
			reaminingCallsThatReachTarget.map(
				(c, i) =>
					i +
					1 +
					'. "' +
					resolveEntityName(c.callee, target.project) +
					'" ' +
					c.callee.type +
					' -- ' +
					c.callee.id
			)
		);
		Logger.log('----------------------------------------');
	}

	if (reaminingCallsThatReachTarget.length === 0) {
		return {
			ancestor: null,
			callType: null
		};
	}

	// Remove any chain of callers in the list
	// E.g. if A only calls B and B only calls C, we keep only C
	const reducedSingleCallers = reaminingCallsThatReachTarget.filter(
		(callInfo) => {
			const otherCalls = reaminingCallsThatReachTarget
				.filter((otherCallInfo) => otherCallInfo !== callInfo)
				.map(
					(otherCallInfo) => otherCallInfo.callee as CallerEntityState
				);

			if (
				ENTRY_POINT_TYPES.includes(callInfo.callee.type) &&
				(callInfo.callee as EntryPointEntityState).calls.length === 1 &&
				!!otherCalls.find(
					(c) =>
						c ===
						(callInfo.callee as EntryPointEntityState).calls[0]
				)
			) {
				// If the entry point only calls one entity, we remove it
				return false;
			} else if (
				PASS_THROUGH_CALLABLE_TYPES.includes(callInfo.callee.type) &&
				(callInfo.callee as PassThroughCallableEntityState).successCalls
					.length === 1 &&
				!!otherCalls.find(
					(c) =>
						c.id ===
						(callInfo.callee as PassThroughCallableEntityState)
							.successCalls[0]?.id
				)
			) {
				// If the pass-through callable only calls one entity, we remove it
				return false;
			} else if (
				PASS_THROUGH_CALLABLE_TYPES.includes(callInfo.callee.type) &&
				(callInfo.callee as PassThroughCallableEntityState).errorCalls
					.length === 1 &&
				!!otherCalls.find(
					(c) =>
						c.id ===
						(callInfo.callee as PassThroughCallableEntityState)
							.errorCalls[0]?.id
				)
			) {
				// If the pass-through callable only calls one entity, we remove it
				return false;
			}

			return true;
		}
	);

	if (debug) {
		Logger.log(
			'Reduced single callers',
			reducedSingleCallers.length,
			reducedSingleCallers.map(
				(c, i) =>
					i +
					1 +
					'. "' +
					resolveEntityName(c.callee, target.project) +
					'" ' +
					c.callee.type +
					' -- ' +
					c.callee.id
			)
		);
		Logger.log('----------------------------------------');
	}

	if (reducedSingleCallers.length === 0) {
		return {
			ancestor: null,
			callType: null
		};
	}

	if (reducedSingleCallers.length === 1) {
		const singleCall = reducedSingleCallers[0];

		if (singleCall.callee.type === EntityType.Loop) {
			if (singleCall.callee.body.calls.includes(sanitizedTarget)) {
				return {
					ancestor: singleCall.callee as CallerEntityState,
					callType: 'loop-body'
				};
			}
		} else if (ENTRY_POINT_TYPES.includes(singleCall.callee.type)) {
			if (
				(singleCall.callee as CallerEntityState).type ===
					EntityType.FunctionDeclaration &&
				(singleCall.callee as CallerEntityState).parent.type ===
					EntityType.Loop
			) {
				return {
					ancestor: (singleCall.callee as CallerEntityState)
						.parent as CallerEntityState,
					callType: 'loop-body'
				};
			} else {
				return {
					ancestor: singleCall.callee as CallerEntityState,
					callType: 'entry'
				};
			}
		} else if (
			PASS_THROUGH_CALLABLE_TYPES.includes(singleCall.callee.type)
		) {
			if (
				singleCall.callType === 'success' &&
				singleCall.callee.type === EntityType.InternalCall
			) {
				return {
					ancestor: singleCall.callee as CallerEntityState,
					callType: 'success'
				};
			} else if (
				singleCall.callType === 'error' &&
				singleCall.callee.type === EntityType.InternalCall
			) {
				return {
					ancestor: singleCall.callee as CallerEntityState,
					callType: 'error'
				};
			}
		}
	}

	let finalSingleCaller = startPoint;
	let finalCallType: 'success' | 'error' | 'entry' | 'loop-body' | null =
		null;
	let currCaller = reducedSingleCallers[0].callee;
	// Removed first element, as we already used it
	let otherCalls = reducedSingleCallers.slice(1);
	let iteration = -1;

	while (otherCalls.length) {
		iteration++;

		const combinedNextSingleCaller = findNextCombinedSingleCallerAncestor(
			currCaller as CallableEntityState,
			otherCalls[0].callee as CallableEntityState,
			startPoint
		);

		if (combinedNextSingleCaller?.ancestor?.id === startPoint.id) {
			return {
				ancestor: startPoint as CallerEntityState,
				callType: combinedNextSingleCaller.callType
			};
		}

		if (!!combinedNextSingleCaller.ancestor) {
			finalSingleCaller = combinedNextSingleCaller.ancestor;
			currCaller = combinedNextSingleCaller.ancestor;
			finalCallType = combinedNextSingleCaller.callType;
			if (debug) {
				Logger.log(
					'Combined single caller between "' +
						resolveEntityName(currCaller, sanitizedTarget.project) +
						'" ',
					currCaller.type + ' -- ' + currCaller.id,
					'\n',
					'AND "' +
						resolveEntityName(
							otherCalls[0].callee as CallerEntityState,
							sanitizedTarget.project
						) +
						'" ',
					'-> "' +
						resolveEntityName(
							finalSingleCaller as CallerEntityState,
							sanitizedTarget.project
						) +
						'" ',
					finalSingleCaller?.type +
						' -- ' +
						finalSingleCaller?.id +
						' [' +
						finalCallType +
						']\n'
				);
			}
		} else {
			if (debug) {
				Logger.log(
					'NO combined result "' +
						resolveEntityName(currCaller, sanitizedTarget.project) +
						'" ',
					currCaller.type + ' -- ' + currCaller.id,
					'\n',
					'AND "' +
						resolveEntityName(
							otherCalls[0].callee as CallerEntityState,
							sanitizedTarget.project
						) +
						'" '
				);
			}
		}

		// Remove the first element, as we already used it
		otherCalls = otherCalls.slice(1);
	}

	return {
		ancestor: finalSingleCaller as CallerEntityState,
		callType: finalCallType
	};
}

export function getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders(
	entity: PassThroughCallableEntityWithOutputsState | OutputMapState
): {
	ancestor: CallerEntityState | null;
	callType: 'success' | 'error' | 'entry' | 'loop-body' | null;
} {
	if (
		![
			...PASS_THROUGH_CALLABLE_TYPES_WITH_OUTPUTS,
			EntityType.OutputMap
		].includes(entity.type)
	) {
		throw new Error(
			`[getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders] ${
				entity.type
			}: ${entity.id} - ${resolveEntityName(
				entity,
				entity.project
			)} - Can only be used with a value writer entity`
		);
	}

	let ownCaller!: CallableEntityState;

	if (PASS_THROUGH_CALLABLE_TYPES_WITH_OUTPUTS.includes(entity.type)) {
		ownCaller = entity as PassThroughCallableEntityWithOutputsState;
	} else if (entity.type === EntityType.OutputMap) {
		ownCaller = entity.parent as PassThroughCallableEntityState;
	}

	if (!ownCaller) {
		throw new Error(
			`[getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders] ${
				entity.type
			}: ${entity.id} - ${resolveEntityName(
				entity,
				entity.project
			)} - Can only be used with a value writer entity that has a caller`
		);
	}

	const flattenedValueReaders = filterOutDuplicateEntities([
		entity,
		...flattenWrites(entity),
		...(VARIABLE_TYPES.includes(entity.type)
			? (entity as VariableState).outputs.flatMap((output) =>
					flattenWrites(output)
				)
			: [])
	]) as (
		| ValueReadingEntityState
		| VariableDeclarationState
		| OutputMapState
	)[];

	const callers: CallableEntityState[] = filterOutDuplicateEntities(
		flattenedValueReaders
			.map((reader) => getParentExecutableEntity(reader))
			.flatMap((ent) => {
				if (TERMINATION_TYPES.includes(ent?.type as EntityType)) {
					return (ent as TerminationEntityState).calledBy;
				}

				return [ent as CallerEntityState];
			})

		// The goal is to locate the position where the current entity should be declared,
		// Therefore we need to considere all nested calls, since that will determine their own position of declaration
		// Which means, the current entity needs to be declared before any of its callees
	).flatMap((caller) => {
		if (PASS_THROUGH_CALLABLE_TYPES.includes(caller.type)) {
			const successCalls = flattenCalls(
				(caller as PassThroughCallableEntityState).successCalls
			);

			const errorCalls = flattenCalls(
				(caller as PassThroughCallableEntityState).errorCalls
			);

			return [caller, ...successCalls, ...errorCalls];
		}

		return [caller];
	}) as CallableEntityState[];

	// if there's only one caller, it's the earliest by default
	if (callers.length === 1) {
		const caller = callers[0];

		if (ENTRY_POINT_TYPES.includes(caller.type)) {
			if (caller.parent.type === EntityType.Loop) {
				// If the caller is a loop, we return it as a loop-body caller
				return {
					ancestor: caller.parent as CallerEntityState,
					callType: 'loop-body'
				};
			}
			return {
				ancestor: caller as CallerEntityState,
				callType: 'entry'
			};
		}

		if (ownCaller === caller) {
			// If the caller is the same as the own caller, we return it as a same-entity
			return {
				ancestor: caller as CallerEntityState,
				callType: 'success'
			};
		}

		if (
			(caller as PassThroughCallableEntityState).successCalls.includes(
				ownCaller
			)
		) {
			return {
				ancestor: caller as CallerEntityState,
				callType: 'success'
			};
		} else if (
			(caller as PassThroughCallableEntityState).errorCalls.includes(
				ownCaller
			)
		) {
			return {
				ancestor: caller as CallerEntityState,
				callType: 'error'
			};
		}

		throw new Error(
			`[getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders] Entity: ${
				entity.id
			} - ${resolveEntityName(
				entity,
				entity.project
			)} - Could not be resolved, single caller found, but it is not a valid caller: ${
				caller.id
			} - ${resolveEntityName(caller, ownCaller.project)}`
		);
	}

	const earliestOfEachCaller = callers
		.map((caller) => {
			if (ENTRY_POINT_TYPES.includes(caller.type)) {
				if (caller.parent.type === EntityType.Loop) {
					// If the caller is a loop, we return it as a loop-body caller
					return {
						ancestor: caller.parent as CallerEntityState,
						callType: 'loop-body'
					};
				} else {
					return {
						ancestor: caller as CallerEntityState,
						callType: 'entry'
					};
				}
			}

			if (
				caller.parent === entity.project &&
				!ownCaller.calledBy?.length
			) {
				if (caller === ownCaller) {
					return {
						ancestor: caller as CallerEntityState,
						callType: 'success'
					};
				}

				// Flatten success and error calls to see from which the own caller is called
				const successCalls = flattenCalls(
					(caller as PassThroughCallableEntityState).successCalls
				);

				const errorCalls = flattenCalls(
					(caller as PassThroughCallableEntityState).errorCalls
				);

				if (successCalls.includes(ownCaller)) {
					return {
						ancestor: caller as CallerEntityState,
						callType: 'success'
					};
				} else if (errorCalls.includes(ownCaller)) {
					return {
						ancestor: caller as CallerEntityState,
						callType: 'error'
					};
				}
			}

			const result = findNextSingleCallerAncestor(
				caller as CallableEntityState
			);

			if (!result.ancestor) {
				throw new Error(
					`[getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders] Entity: ${
						entity.id
					} - ${resolveEntityName(
						entity,
						entity.project
					)} - Could not be resolved, no ancestor found for entity ${
						caller.id
					} -- ${resolveEntityName(caller, ownCaller.project)}`
				);
			}

			return result;
		})
		.filter(
			// Filter out duplicates
			(callInfo, index, self) =>
				index ===
				self.findIndex(
					(info) => info.ancestor?.id === callInfo.ancestor?.id
				)
		);

	const commonEntryPoint = getParentEntryPoint(ownCaller);

	// Filter out dependent callers
	const filteredCalls = earliestOfEachCaller.filter((callerInfo) => {
		if (!callerInfo?.ancestor) {
			return false;
		}

		if (commonEntryPoint === callerInfo.ancestor) {
			// If the caller is an entry point, we can consider it as independent
			return true;
		}

		const otherCallers = earliestOfEachCaller
			.filter(
				(otherCallerInfo) =>
					otherCallerInfo.ancestor !== callerInfo.ancestor
			)
			.map(
				(otherCallerInfo) =>
					otherCallerInfo.ancestor as CallerEntityState
			);

		const anyIsBeforeSelf = otherCallers.some((flatCall) => {
			const foundAsAncestor = flattenCallerAncestorsUntil(
				callerInfo.ancestor as CallableEntityState,
				flatCall
			);

			return !!foundAsAncestor?.last;
		});

		return !anyIsBeforeSelf;
	});

	if (filteredCalls.length === 0) {
		return {
			ancestor:
				commonEntryPoint?.parent.type === EntityType.Loop
					? (commonEntryPoint?.parent as CallerEntityState)
					: commonEntryPoint,
			callType:
				commonEntryPoint?.parent.type === EntityType.Loop
					? 'loop-body'
					: 'entry'
		};
	}

	if (filteredCalls.length > 1) {
		throw new Error(
			`[getEarliestSingleCallerAncestorOfValueWriterAndItsValueReaders] Entity: ${
				entity.id
			} - ${resolveEntityName(
				entity,
				entity.project
			)} - Could not be resolved, multiple candidates found: ${filteredCalls
				.map((callInfo) =>
					resolveEntityName(
						callInfo.ancestor as CallableEntityState,
						ownCaller.project
					)
				)
				.join(', ')}`
		);
	}

	return {
		ancestor: filteredCalls[0].ancestor as CallerEntityState,
		callType: filteredCalls[0].callType as
			| 'success'
			| 'error'
			| 'entry'
			| 'loop-body'
	};
}

export async function removeRedundantCallers(
	entity: CallableEntityState,
	changeSet: ChangeSet | null,
	options?: {
		onCallRemoved?: (
			caller: CallerEntityState,
			callee: CallableEntityState,
			callType: 'success' | 'error' | 'entry' | 'loop-body'
		) => Promise<void>;
	}
): Promise<ChangeSet | null> {
	const optionsWithDefaults = {
		onCallRemoved: async () => {},
		...options
	};

	const callers = entity.calledBy;

	if (callers.length <= 1) {
		return changeSet;
	}

	// If any of its inmediate callers, has any od the other callers as its ancestor, we remove it.
	const redundantCallers = callers.filter((caller) => {
		const flatCalls = flattenElementCalls(caller);

		// If the current caller has descendants that are also callers of the target entity, then it is not redundant, since it is the one that reaches those callers
		const isRedundant = flatCalls.some((otherCaller) => {
			if (TERMINATION_TYPES.includes(otherCaller.type)) {
				return false;
			}

			const otherCallerCalls = flattenElementCalls(
				otherCaller as CallerEntityState
			);

			if (otherCallerCalls.find((c) => c.id === entity.id)) {
				return true;
			}

			return false;
		});

		return isRedundant;
	});

	for (const redundantCaller of redundantCallers) {
		if (ENTRY_POINT_TYPES.includes(redundantCaller.type)) {
			redundantCaller.removeCall(entity, changeSet);
			entity.removeCaller(redundantCaller, changeSet);

			await optionsWithDefaults.onCallRemoved(
				redundantCaller,
				entity,
				'entry'
			);
		} else if (PASS_THROUGH_CALLABLE_TYPES.includes(redundantCaller.type)) {
			let callType: 'success' | 'error' = 'success';

			if (
				(
					redundantCaller as PassThroughCallableEntityState
				).errorCalls.includes(entity)
			) {
				callType = 'error';
			}

			(redundantCaller as PassThroughCallableEntityState).removeCall(
				entity,
				changeSet
			);
			entity.removeCaller(redundantCaller, changeSet, true);

			await optionsWithDefaults.onCallRemoved(
				redundantCaller,
				entity,
				callType
			);
		}
	}

	return changeSet;
}

/**
 * Sometimes a value reading entity might be reading values from an entity which its is not allowed.
 * All value reading entities need to be called-by, directly or indirectly, by a value writing entity.
 * For example: An input-map of an operation that reads the value of a variable-instance. The var instance needs to call the operation
 * If the operation is calling the var instance, or an ancestor of it, then it is not valid and we can't fix it
 *
 * @param {ValueReadingEntityState} entity
 * @param {ChangeSet | null} changeSet
 *
 * @returns { valid: boolean; }
 */
export function bestEffortRecalculateCallerForExistingValueWriter(
	entity: ValueReadingEntityState,
	changeSet: ChangeSet | null
): { valid: boolean; newCallerEntity: CallerEntityState | null } {
	let valid = true;

	if (!VALUE_READING_TYPES.includes(entity.type)) {
		throw new Error(
			`[bestEffortRecalculateCallerForExistingValueWriter] Entity of type ${entity.type} is not a value reading entity`
		);
	}

	const parentCallableEntity = getParentCallableEntity(entity);

	if (!parentCallableEntity) {
		throw new Error(
			`[bestEffortRecalculateCallerForExistingValueWriter] Could not find parent callable entity for entity ${entity.type} ${entity.id}`
		);
	}

	if (!entity.readsValue) {
		return { valid, newCallerEntity: null };
	}

	const writersCallerParent = getParentCallerEntity(entity.readsValue);

	if (!writersCallerParent) {
		throw new Error(
			`[bestEffortRecalculateCallerForExistingValueWriter] Could not find caller entity for parent callable entity ${parentCallableEntity.type} ${parentCallableEntity.id}`
		);
	}

	const { last } = flattenCallerAncestorsUntil(
		parentCallableEntity,
		writersCallerParent
	);

	if (!last || last.id !== writersCallerParent.id) {
		if (PASS_THROUGH_CALLABLE_TYPES.includes(writersCallerParent.type)) {
			(
				writersCallerParent as PassThroughCallableEntityState
			).addSuccessCall(parentCallableEntity, changeSet);

			parentCallableEntity.addSuccessCaller(
				writersCallerParent as PassThroughCallableEntityState,
				changeSet,
				true
			);
			return {
				valid,
				newCallerEntity:
					writersCallerParent as PassThroughCallableEntityState
			};
		} else if (ENTRY_POINT_TYPES.includes(writersCallerParent.type)) {
			(writersCallerParent as EntryPointEntityState).addCall(
				parentCallableEntity,
				changeSet
			);

			parentCallableEntity.addEntryCaller(
				writersCallerParent as EntryPointEntityState
			);
			return {
				valid,
				newCallerEntity: writersCallerParent as EntryPointEntityState
			};
		}
	}

	return { valid, newCallerEntity: null };
}

/**
 * Recursively resolves all entities that can throw errors and don't catch them, or call logic upon error
 * from the entry point, and without own ancestors or conditions or itself
 *
 * @param {CallableEntityState} entity The entity to resolve pass through entities with outputs for
 * @returns {{ ancestor: PassThroughCallableEntityWithOutputsState, call: CallableEntityState, callType: ('success'|'error') }[]} An array of independent parallel conditions direct calls
 */
export function resolveAllParallelAncestorErrorCallersForEntity(
	entity: CallableEntityState
): {
	caller: PassThroughCallableEntityWithOutputsState;
}[] {
	const seen = new Set<string>();
	const parallelErrorThrowers: {
		caller: PassThroughCallableEntityWithOutputsState;
	}[] = [];

	if (!entity.calledBy.length) {
		return [];
	}

	if (!CALLABLE_TYPES.includes(entity.type)) {
		throw new Error(
			`[resolveAllParallelAncestorErrorCallersForEntity] Entity of type ${entity.type}: "${resolveEntityName(entity, entity.project)}" with id ${entity.id} is not a callable entity`
		);
	}

	const scopeOwner = getScopeOwner(entity, entity.project);

	if (!scopeOwner) {
		throw new Error(
			`[resolveAllParallelAncestorErrorCallersForEntity] No entry point entity found for ${entity.type}: "${resolveEntityName(
				entity,
				entity.project
			)}" -- with id ${entity.id}`
		);
	}

	let branchRoot!: CallerEntityState;

	if (scopeOwner.type === EntityType.Project) {
		let currentCallee = entity;

		while (!!currentCallee) {
			if (!currentCallee.calledBy.length) {
				break;
			}

			const foundEntryPoint = currentCallee.calledBy.find((c) =>
				ENTRY_POINT_TYPES.includes(c.type)
			);

			if (!!foundEntryPoint) {
				branchRoot = foundEntryPoint;

				break;
			} else {
				const caller = currentCallee.calledBy[0];

				if (CALLABLE_TYPES.includes(caller.type)) {
					currentCallee = caller as CallableEntityState;
				} else {
					branchRoot = caller;

					break;
				}
			}
		}

		if (!branchRoot && CALLER_TYPES.includes(currentCallee.type)) {
			branchRoot = currentCallee as CallerEntityState;
		}

		if (!branchRoot) {
			return [];
		}
	} else {
		branchRoot = scopeOwner;
	}

	const flatCalls: {
		callee: CallableEntityState;
		callType: 'success' | 'error' | 'entry' | 'loop-body';
	}[] = [];

	if (scopeOwner.type === EntityType.Loop) {
		const filteredEntities = filterOutDuplicateEntities(
			flattenCalls(scopeOwner.body.calls, {
				excludeLoopBodies: true
			})
		) as CallableEntityState[];

		flatCalls.push(
			...filteredEntities.map((callee) => ({
				callee,
				callType: 'loop-body' as const
			}))
		);
	} else {
		flatCalls.push(
			...flattenElementCallsWithCallType(branchRoot, {
				excludeLoopBodies: true
			})
		);
	}

	const ownFlatCalls = TERMINATION_TYPES.includes(entity.type)
		? []
		: flattenElementCallsWithCallType(entity as CallerEntityState, {
				excludeLoopBodies: true
			});

	const ownFlatCallers = flattenCallersWithCallType(entity);
	const ownErrorCallers = ownFlatCallers.filter(
		(c) => c.callType === 'error' && c.caller.type !== EntityType.Condition
	);

	flatCalls.forEach((callee) => {
		if (callee.callee?.id === entity?.id) {
			return;
		} else if (TERMINATION_TYPES.includes(callee.callee?.type)) {
			return;
		} else if (callee.callee?.type === EntityType.Condition) {
			return;
		}

		const currentCalls = !!ownFlatCalls.find(
			(call) => call.callee.id === callee.callee.id
		);

		if (currentCalls) {
			return;
		}

		const asPassThrough =
			callee.callee as PassThroughCallableEntityWithOutputsState;

		// If the error ends the execution flow, skip it
		if (!asPassThrough.catchesError && !asPassThrough.errorCalls.length) {
			return;
		}

		const calleeFlatCalls = flattenElementCallsWithCallType(asPassThrough);

		const callsCurAsSuccess = !!calleeFlatCalls.find(
			(cur) => cur.callee?.id === entity?.id && cur.callType === 'success'
		);

		if (!!callsCurAsSuccess) {
			if (seen.has(asPassThrough.id)) {
				return;
			}

			seen.add(asPassThrough.id);
			// By definition, the current callee has an error branch and the current entity isn't in it
			parallelErrorThrowers.push({
				caller: asPassThrough
			});
			return;
		}

		// If it calls the current entity as error, skip
		const callsCurAsError = !!calleeFlatCalls.find(
			(cur) => cur.callee?.id === entity?.id && cur.callType === 'error'
		);

		if (callsCurAsError) {
			return;
		}

		const calleeFlatCallers = flattenCallersWithCallType(asPassThrough);

		const calleeCalledByErrors = calleeFlatCallers.filter(
			(c) =>
				c.callType === 'error' && c.caller.type !== EntityType.Condition
		);

		if (!!calleeCalledByErrors.length || !!ownErrorCallers.length) {
			const allSameErrorCallers =
				ownErrorCallers.every(
					(c) =>
						!!calleeCalledByErrors.find(
							(calleeErrorCaller) =>
								c.caller.id === calleeErrorCaller.caller.id
						)
				) &&
				calleeCalledByErrors.every(
					(c) =>
						!!ownErrorCallers.find(
							(ownErrorCaller) =>
								c.caller.id === ownErrorCaller.caller.id
						)
				);

			if (allSameErrorCallers) {
				return;
			}
		}

		if (seen.has(asPassThrough.id)) {
			return;
		}

		seen.add(asPassThrough.id);
		parallelErrorThrowers.push({
			caller: asPassThrough
		});
	});

	return parallelErrorThrowers;
}
